import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import PurchaseHistory from "./PurchaseHistory.jsx";
import PaymentTracking from "./PaymentTracking.jsx";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

export default function AdminPanel() {

// Provides a horizontal scrollbar at the top of a table (so you don't have to scroll to the bottom),
// and freezes the header row when scrolling vertically.
// Keep the component identity stable so table inputs do not lose focus while typing.
const TableScrollContainer = useMemo(() => {
  return function TableScrollContainer({ children, maxHeightClass = "max-h-[70vh]" }) {
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const syncingRef = useRef(false);

  useEffect(() => {
    const bodyEl = bodyRef.current;
    if (!bodyEl) return;

    const update = () => {
      // Prefer the actual content width (table) so the top scrollbar always matches the bottom scrollbar.
      const inner = bodyEl.firstElementChild;
      const sw = (inner && inner.scrollWidth) ? inner.scrollWidth : bodyEl.scrollWidth;
      setContentWidth(sw || 0);
    };

    update();
    window.addEventListener("resize", update);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(bodyEl);
      if (bodyEl.firstElementChild) ro.observe(bodyEl.firstElementChild);
    }

    return () => {
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
    };
  }, []);

  const handleTopScroll = () => {
    if (syncingRef.current) return;
    const topEl = topRef.current;
    const bodyEl = bodyRef.current;
    if (!topEl || !bodyEl) return;
    syncingRef.current = true;
    bodyEl.scrollLeft = topEl.scrollLeft;
    syncingRef.current = false;
  };

  const handleBodyScroll = () => {
    if (syncingRef.current) return;
    const topEl = topRef.current;
    const bodyEl = bodyRef.current;
    if (!topEl || !bodyEl) return;
    syncingRef.current = true;
    topEl.scrollLeft = bodyEl.scrollLeft;
    syncingRef.current = false;
  };

  return (
    <div className="w-full">
      <div
        ref={topRef}
        onScroll={handleTopScroll}
        className="w-full overflow-x-auto overflow-y-hidden h-4"
      >
        <div style={{ width: contentWidth ? `${contentWidth}px` : "100%" }} />
      </div>

      <div
        ref={bodyRef}
        onScroll={handleBodyScroll}
        className={`w-full overflow-auto ${maxHeightClass}`}
      >
        {children}
      </div>
    </div>
  );
  };
}, []);
  // Row background colors by status/decision (Project Approvals table only)
  const hexToRgba = (hex, alpha) => {
    const clean = String(hex || "").replace("#", "").trim();
    if (clean.length !== 6) return undefined;
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getProjectRequestRowStyle = (statusRaw, userDecision) => {
    const s = String(statusRaw || "");
    const d = String(userDecision || "");
    if (d === "accepted" || s === "Approved") {
      return { backgroundColor: hexToRgba("#94c83d", 0.18) };
    }
    if (s === "Sent to Ops") {
      return { backgroundColor: hexToRgba("#94c83d", 0.18) };
    }
    if (d === "rejected" || s === "Rejected") {
      return { backgroundColor: "rgba(156, 163, 175, 0.22)" };
    }
    if (s === "Cost Submitted") {
      return { backgroundColor: "rgba(249, 115, 22, 0.18)" }; // orange
    }
    if (s === "Open Request") {
      return { backgroundColor: hexToRgba("#f5b800", 0.18) }; // yellow
    }
    return undefined;
  };


  const { jwt, user } = useAuth();

// Keep AdminPanel tab / edit screen in sync with the URL (so browser Back returns to Project Requests).
useEffect(() => {
  applyAdminUrlState();
  const onPop = () => applyAdminUrlState();
  window.addEventListener("popstate", onPop);
  return () => window.removeEventListener("popstate", onPop);
  // eslint-disable-next-line
}, []);

  const isUserSuper = Boolean(user?.is_super_admin);
  const isUserAdmin = Boolean(user?.is_admin);
  const isUserAssistant = Boolean(user?.is_assistant);
  const isAssistantOnly = Boolean(isUserAssistant && !isUserAdmin && !isUserSuper);

  // Tab state
  const [tab, setTab] = useState(isAssistantOnly ? "requests" : "users"); // "users" | "history" | "drafts" | "requests" | "audit"
const syncAdminUrl = (nextTab, editTaskId, replace) => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", String(nextTab || "users"));
    if (editTaskId) {
      url.searchParams.set("edit", String(editTaskId));
    } else {
      url.searchParams.delete("edit");
    }
    if (replace) {
      window.history.replaceState({}, "", url.toString());
    } else {
      window.history.pushState({}, "", url.toString());
    }
  } catch {
    // ignore
  }
};

const applyAdminUrlState = () => {
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const t = sp.get("tab");
    const e = sp.get("edit");
    const allowed = ["users", "drafts", "requests", "history", "payments", "audit", "abuse", "suppressed"];
    const resolvedTab = String(t) === "suppressed" ? "abuse" : String(t);
    const nextTab = allowed.includes(resolvedTab) ? resolvedTab : (isAssistantOnly ? "requests" : "users");

    // If an edit task is present in the URL, we force the Requests tab and open the edit screen.
    if (e) {
      setTab("requests");
      setPendingEditTaskId(String(e));
      return;
    }

    // Otherwise, clear any pending edit state and just set the tab from the URL.
    setPendingEditTaskId("");
    setEditCostsModalOpen(false);
    setEditCostsRequest(null);
    setTab(nextTab);
  } catch (e) {
    setPendingEditTaskId("");
    setEditCostsModalOpen(false);
    setEditCostsRequest(null);
    setTab(isAssistantOnly ? "requests" : "users");
  }
};



  useEffect(() => {
    if (isAssistantOnly && tab !== "requests") {
      setTab("requests");
    }
  }, [isAssistantOnly, tab]);

  const [users, setUsers] = useState([]);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [message, setMessage] = useState("");
  const [creditsToSet, setCreditsToSet] = useState({});
  const [editFields, setEditFields] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  
  // Draft Users
  const [drafts, setDrafts] = useState([]);
  const [draftSearch, setDraftSearch] = useState("");
  const [draftPage, setDraftPage] = useState(1);
  const [draftSize, setDraftSize] = useState(25);
  const [draftLoading, setDraftLoading] = useState(false);

  // Abuse Protection
  const [abuseData, setAbuseData] = useState({
    stats: {},
    blocked_emails: [],
    blocked_email_domains: [],
    blocked_ips: [],
    events: [],
    webhook_history: [],
    threat_intel: { feeds: [], allowlist: [] },
  });
  const [abuseLoading, setAbuseLoading] = useState(false);
  const [abuseSearch, setAbuseSearch] = useState("");
  const [blockedEmailSearch, setBlockedEmailSearch] = useState("");
  const [blockedEmailDisplaySize, setBlockedEmailDisplaySize] = useState(50);
  const [abuseSection, setAbuseSection] = useState("suppressed");
  const [newBlockedEmail, setNewBlockedEmail] = useState("");
  const [newBlockedEmailReason, setNewBlockedEmailReason] = useState("");
  const [newBlockedIP, setNewBlockedIP] = useState("");
  const [newBlockedIPReason, setNewBlockedIPReason] = useState("");
  const [blockedEmailImportFile, setBlockedEmailImportFile] = useState(null);
  const [blockedEmailImportReason, setBlockedEmailImportReason] = useState("Imported by administrator");
  const [blockedIPImportFile, setBlockedIPImportFile] = useState(null);
  const [blockedIPImportReason, setBlockedIPImportReason] = useState("Imported by administrator");
  const [abuseActionLoading, setAbuseActionLoading] = useState(false);
  const [threatAllowType, setThreatAllowType] = useState("ip");
  const [threatAllowValue, setThreatAllowValue] = useState("");
  const [threatAllowReason, setThreatAllowReason] = useState("");
  const blockedEmailImportRef = useRef(null);
  const blockedIPImportRef = useRef(null);

  // Admin Admin History
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditAction, setAuditAction] = useState("");
  const [auditTargetEmail, setAuditTargetEmail] = useState("");
  const [auditDays, setAuditDays] = useState(30);

  // Project Requests
  const [projectRequests, setProjectRequests] = useState([]);
  const [projectRequestsLoading, setProjectRequestsLoading] = useState(false);

  // Project Approvals table sorting
  const [approvalsSortKey, setApprovalsSortKey] = useState("date"); // "estimate" | "user" | "date" | "status"
  const [approvalsSortDir, setApprovalsSortDir] = useState("desc"); // "asc" | "desc"

  // Users table sorting
  const [usersSortKey, setUsersSortKey] = useState("last"); // "last" | "first" | "email" | "type" | "confirmed"
  const [usersSortDir, setUsersSortDir] = useState("asc"); // "asc" | "desc"

  // Draft Users table sorting
  const [draftsSortKey, setDraftsSortKey] = useState("updated"); // "updated" | "email" | "name" | "company" | "step" | "status"
  const [draftsSortDir, setDraftsSortDir] = useState("desc"); // "asc" | "desc"


  const updateProjectRequestRow = (taskId, patch) => {
    if (!taskId) return;
    setProjectRequests(prev => prev.map(r => {
      const rid = r?.task_id;
      if (!rid || String(rid) !== String(taskId)) return r;
      return { ...r, ...patch };
    }));
  };

  const formatMoneyUSD = (n) => {
    try {
      const num = Number(n || 0);
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch {
      return "0.00";
    }
  };

  const formatMoneyInput = (v) => {
    try {
      const s = String(v ?? "").replace(/,/g, "").trim();
      if (!s) return "";
      const num = Number(s);
      if (Number.isNaN(num)) return "";
      return num.toFixed(2);
    } catch {
      return "";
    }
  };

  const parseMoneyNumber = (v) => {
    try {
      if (v === null || v === undefined) return 0;
      if (typeof v === "number") return Number.isFinite(v) ? v : 0;
      const s = String(v).replace(/,/g, "").replace(/\$/g, "").trim();
      if (!s) return 0;
      const n = Number(s);
      return Number.isNaN(n) ? 0 : n;
    } catch {
      return 0;
    }
  };

  const getEstimatedRangeText = (totalValue) => {
    try {
      const totalNum = Number(String(totalValue ?? "").replace(/[$,]/g, "").trim());
      if (!Number.isFinite(totalNum) || totalNum <= 0) return "";
      const low = totalNum * 0.90;
      const high = totalNum * 1.25;
      const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return `${fmt(low)} — ${fmt(high)}`;
    } catch {
      return "";
    }
  };

  const getCostsFromBreakdown = (lines) => {
    // Supports both:
    // 1) string lines like "• AI Translation: $0.07"
    // 2) object lines like { label: "AI Translation", value: 0.07, display: "$0.07" }
    const out = { ai: null, review: null, fmt: null };
    const arr = Array.isArray(lines) ? lines : [];

    const takeFromObj = (obj) => {
      const label = String(obj?.label || "").toLowerCase();
      if (!label) return;
      const val = (obj?.value !== undefined && obj?.value !== null) ? Number(obj.value) : null;
      if (val === null || Number.isNaN(val)) return;

      if (label.includes("ai")) out.ai = val;
      else if (label.includes("limited review") || label.includes("human review") || label.includes("review")) out.review = val;
      else if (label.includes("format")) out.fmt = val;
    };

    const takeFromStr = (s0) => {
      const s = String(s0 || "");
      if (!s) return;

      const low = s.toLowerCase();
      const m = s.match(/\$?\s*([0-9]+(?:\.[0-9]{1,2})?)/);
      if (!m) return;
      const val = Number(m[1]);
      if (Number.isNaN(val)) return;

      if (low.includes("ai")) out.ai = val;
      else if (low.includes("limited review") || low.includes("human review") || low.includes("review")) out.review = val;
      else if (low.includes("format")) out.fmt = val;
    };

    for (const line of arr) {
      if (line && typeof line === "object" && !Array.isArray(line)) {
        takeFromObj(line);
      } else {
        takeFromStr(line);
      }
    }

    return out;
  };


  // Project Requests table only: compact cost labels (do not change persisted breakdown text).
  const compactCostLineForRequestsTable = (line) => {
    try {
      let s = String(line || "");
      if (!s) return "";

      // Normalize any leading bullet (we render bullets ourselves via the text).
      s = s.replace(/^\s*[•\-\*]\s*/, "• ");

      // Keep Total un-bulleted.
      if (/^\s*total\s*:/i.test(s)) {
        return s.replace(/^\s*[•\-\*]\s*/i, "");
      }

      // Remove "Estimated" from review line (Limited Review / Human Review) for this table only.
      s = s.replace(/\bEstimated\s+(Limited Review|Human Review)\b/gi, "$1");

      // Remove "Estimated Post-Translation" prefix for formatting/QC line for this table only.
      s = s.replace(/\bEstimated\s+Post-Translation\s+(Formatting\s*\/\s*QC|Formatting\/QC|Formatting|Formatting\s*\/?\s*QC)\b/gi, (m, p1) => {
        const clean = String(p1 || "Formatting/QC").replace(/\s+/g, " ").replace(/\s*\/\s*/g, "/");
        return clean;
      });

      // If the line doesn't start with a bullet, add it.
      if (!s.trim().startsWith("•")) {
        s = `• ${s.trim()}`;
      }

      return s;
    } catch {
      return String(line || "");
    }
  };

  const buildCostBreakdownForRequestsTable = (r) => {
    const raw = Array.isArray(r?.cost_breakdown) ? r.cost_breakdown : [];
    const lines = raw.map(compactCostLineForRequestsTable).filter(Boolean);

    // Ensure there is a Total line (table only). Prefer the server-provided total alias.
    const hasTotal = lines.some((x) => /^\s*total\s*:/i.test(String(x || "")));
    if (!hasTotal) {
      const totalStr = String(r?.total || "").trim();
      if (totalStr) {
        lines.push(`Total: ${totalStr}`);
      }
    }
    return lines;
  };


  // Edit Costs modal (Project Requests)
  const [editCostsModalOpen, setEditCostsModalOpen] = useState(false);
  const [pendingEditTaskId, setPendingEditTaskId] = useState("");
  const [editCostsRequest, setEditCostsRequest] = useState(null);
  const [editCostsLoadError, setEditCostsLoadError] = useState("");
  const [editCostsAI, setEditCostsAI] = useState("");
  const [editCostsLR, setEditCostsLR] = useState("");
  const [editCostsFmt, setEditCostsFmt] = useState("");
  const [editCostsDate, setEditCostsDate] = useState("");
  const [editCostsDateText, setEditCostsDateText] = useState("");
  const [editCostsSaving, setEditCostsSaving] = useState(false);
  const [editCostsSubmitting, setEditCostsSubmitting] = useState(false);

  // Draft terminology modal state
  const [draftTermModalDraft, setDraftTermModalDraft] = useState(null);
  const [draftTermModalItems, setDraftTermModalItems] = useState([]);

  // Regular users terminology
  const [termModalUser, setTermModalUser] = useState(null);
  const [termModalItems, setTermModalItems] = useState([]);

  // Draft TMX modal state
  const [draftTmxModalDraft, setDraftTmxModalDraft] = useState(null);
  const [draftTmxModalItems, setDraftTmxModalItems] = useState([]);

  // Regular users TMX
  const [tmxModalUser, setTmxModalUser] = useState(null);
  const [tmxModalItems, setTmxModalItems] = useState([]);


  const handleDraftTerminologyClick = async (d) => {
    if (!jwt) {
      window.alert("You must be logged in to view terminology files.");
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/admin/workflow/drafts/${d.id}/terminology`,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );

      const items = Array.isArray(res.data) ? res.data : [];

      if (items.length === 0) {
        window.alert("This draft has no terminology entries.");
        return;
      }

      setDraftTermModalDraft(d);
      setDraftTermModalItems(items);
    } catch (err) {
      console.error("Failed to load draft terminology:", err);
      window.alert("Failed to load terminology for this draft.");
    }
  };

  useEffect(() => {
    if (!jwt) return;
    axios
      .get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(res => setUsers(res.data))
      .catch(() => setUsers([]));
  }, [jwt, refreshFlag]);
  
  useEffect(() => {
    if (!jwt) return;
    if (tab !== "drafts") return;
    setDraftLoading(true);
    const params = new URLSearchParams();
    if (draftSearch.trim()) params.set("q", draftSearch.trim());
    params.set("page", String(draftPage));
    params.set("size", String(draftSize));
    axios.get(`${API_BASE}/admin/workflow/drafts?${params.toString()}`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(res => setDrafts(res.data?.items || []))
      .catch(() => setDrafts([]))
      .finally(() => setDraftLoading(false));
  }, [jwt, tab, draftSearch, draftPage, draftSize, refreshFlag]);

  useEffect(() => {
    if (!jwt) return;
    if (tab !== "abuse") return;
    setAbuseLoading(true);
    axios
      .get(`${API_BASE}/admin/abuse-protection?days=30&limit=500`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => {
        setAbuseData({
          stats: res.data?.stats || {},
          blocked_emails: Array.isArray(res.data?.blocked_emails)
            ? res.data.blocked_emails
            : (Array.isArray(res.data?.suppressed_emails) ? res.data.suppressed_emails : []),
          blocked_email_domains: Array.isArray(res.data?.blocked_email_domains) ? res.data.blocked_email_domains : [],
          blocked_ips: Array.isArray(res.data?.blocked_ips) ? res.data.blocked_ips : [],
          events: Array.isArray(res.data?.events) ? res.data.events : [],
          webhook_history: Array.isArray(res.data?.webhook_history) ? res.data.webhook_history : [],
          threat_intel: res.data?.threat_intel || { feeds: [], allowlist: [] },
        });
      })
      .catch(() =>
        setAbuseData({
          stats: {},
          blocked_emails: [],
          blocked_email_domains: [],
          blocked_ips: [],
          events: [],
          webhook_history: [],
          threat_intel: { feeds: [], allowlist: [] },
        })
      )
      .finally(() => setAbuseLoading(false));
  }, [jwt, tab, refreshFlag]);

  useEffect(() => {
    if (!jwt) return;
    if (tab !== "audit") return;
    setAuditLoading(true);
    const params = new URLSearchParams();
    params.set("days", String(auditDays || 0));
    params.set("limit", "500");
    if (auditSearch.trim()) params.set("q", auditSearch.trim());
    if (auditAction.trim()) params.set("action", auditAction.trim());
    if (auditTargetEmail.trim()) params.set("target_email", auditTargetEmail.trim());
    axios.get(`${API_BASE}/admin/audit-log?${params.toString()}`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(res => setAuditRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAuditRows([]))
      .finally(() => setAuditLoading(false));
  }, [jwt, tab, auditSearch, auditAction, auditTargetEmail, auditDays, refreshFlag]);

  useEffect(() => {
    if (!jwt) return;
    if (tab !== "requests") return;
  setProjectRequestsLoading(true);
  axios.get(`${API_BASE}/admin/project-requests`, {
    headers: { Authorization: `Bearer ${jwt}` }
  })
    .then(res => {
    const data = res && res.data ? res.data : null;
    let list = [];
    if (Array.isArray(data)) {
      list = data;
    } else if (data && Array.isArray(data.items)) {
      list = data.items;
    } else if (data && Array.isArray(data.requests)) {
      list = data.requests;
    } else if (data && Array.isArray(data.results)) {
      list = data.results;
    }
    setProjectRequests(list || []);
    try {
      if (pendingEditTaskId) {
        const match = (Array.isArray(list) ? list : []).find(x => String(x.task_id) === String(pendingEditTaskId));
        // Only auto-open the edit screen if it is not already open.
        if (match && !(editCostsModalOpen && editCostsRequest && String(editCostsRequest.task_id) === String(pendingEditTaskId))) {
          if (!editCostsModalOpen) {
            handleOpenEditCosts(match, true);
          }
        }
        // Prevent re-opening on subsequent refreshes.
        setPendingEditTaskId("");
      }
    } catch {
      // ignore
    }
})
    .catch(() => setProjectRequests([]))
    .finally(() => setProjectRequestsLoading(false));
}, [jwt, tab, refreshFlag]);




  


  useEffect(() => {
    if (!jwt) return;
    if (!editCostsModalOpen) return;
    if (!editCostsRequest || !editCostsRequest.task_id) return;

    setEditCostsLoadError("");

    axios
      .get(`${API_BASE}/admin/project-requests/${encodeURIComponent(String(editCostsRequest.task_id))}`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then((res) => {
        const r = res && res.data ? res.data : null;
        if (!r) return;

        setEditCostsRequest((prev) => ({ ...(prev || {}), ...(r || {}) }));

        // If the server has a completion date and the input is empty, fill it.
        try {
          if (!String(editCostsDate || "").trim()) {
            const dt0 = String(r.estimated_completion_date || "");
            const norm0 = normalizeDateInputValue(dt0);
            setEditCostsDate(norm0);
            setEditCostsDateText(isoToLocaleDateString(norm0));
          }
        } catch {
          // ignore
        }

        // If the user has already typed values, do not overwrite inputs.
        const hasAny = String(editCostsAI || "") || String(editCostsLR || "") || String(editCostsFmt || "");
        if (hasAny) return;

        const lines =
          (Array.isArray(r.final_cost_breakdown) && r.final_cost_breakdown.length)
            ? r.final_cost_breakdown
            : ((Array.isArray(r.cost_breakdown) && r.cost_breakdown.length) ? r.cost_breakdown : []);

        const extracted = getCostsFromBreakdown(lines);

        setEditCostsAI(extracted.ai !== null && extracted.ai !== undefined ? formatMoneyInput(String(extracted.ai)) : "");
        setEditCostsLR(extracted.review !== null && extracted.review !== undefined ? formatMoneyInput(String(extracted.review)) : "");
        setEditCostsFmt(extracted.fmt !== null && extracted.fmt !== undefined ? formatMoneyInput(String(extracted.fmt)) : "");

        const dt = String(r.estimated_completion_date || "");
                const norm = normalizeDateInputValue(dt);
        setEditCostsDate(norm);
                setEditCostsDateText(isoToLocaleDateString(norm));


      setEditCostsLoadError("");
      })
      .catch((err) => {
        try {
          console.error("Edit Costs load failed", err);
        } catch {
          // ignore
        }
        // If we already have a usable breakdown from the table row, don't show the scary banner.
        const fbFinal = Array.isArray(editCostsRequest?.final_cost_breakdown) ? editCostsRequest.final_cost_breakdown : [];
        const fbEstimate = Array.isArray(editCostsRequest?.cost_breakdown) ? editCostsRequest.cost_breakdown : [];
        const hasInputs = Boolean(String(editCostsAI || "").trim() || String(editCostsLR || "").trim() || String(editCostsFmt || "").trim());
        if (!fbFinal.length && !fbEstimate.length && !hasInputs) {
          setEditCostsLoadError("Could not load full request details. You can still edit and submit costs; if this persists, refresh and try again.");
        }
      });
  }, [jwt, editCostsModalOpen, editCostsRequest && editCostsRequest.task_id]);

  
  const normalizeDateInputValue = (dt) => {
    try {
      const s = String(dt || "").trim();
      if (!s) return "";
      // ISO datetime -> ISO date
      if (s.indexOf("T") !== -1) return s.split("T")[0];
      if (s.indexOf(" ") !== -1) return s.split(" ")[0];

      // mm/dd/yyyy -> yyyy-mm-dd
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) {
        const mm = String(m[1]).padStart(2, "0");
        const dd = String(m[2]).padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
      }

      // yyyy-mm-dd already
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

      // Best-effort parse
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      return s;
    } catch {
      return "";
    }
  };
  const getLocaleDatePlaceholder = () => {
    try {
      // Build a placeholder that matches the viewer's locale order (MM/DD/YYYY vs DD/MM/YYYY, etc.).
      const parts = new Intl.DateTimeFormat(undefined).formatToParts(new Date(2000, 10, 22)); // Nov 22, 2000
      const out = [];
      for (const p of parts) {
        if (p.type === "month") out.push("MM");
        else if (p.type === "day") out.push("DD");
        else if (p.type === "year") out.push("YYYY");
        else if (p.type === "literal") out.push(p.value);
      }
      const s = out.join("").trim();
      return s || "MM/DD/YYYY";
    } catch {
      return "MM/DD/YYYY";
    }
  };

  const isoToLocaleDateString = (iso) => {
    try {
      const s = String(iso || "").trim();
      if (!s) return "";
      const d = new Date(`${s}T00:00:00`);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString();
    } catch {
      return "";
    }
  };

  const parseLocaleDateToISO = (s0) => {
    try {
      const s = String(s0 || "").trim();
      if (!s) return "";

      // Allow ISO YYYY-MM-DD as input.
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

      const ph = getLocaleDatePlaceholder();
      const order = [];
      // Determine order from placeholder tokens.
      if (ph.indexOf("MM") !== -1) order.push({ t: "M", i: ph.indexOf("MM") });
      if (ph.indexOf("DD") !== -1) order.push({ t: "D", i: ph.indexOf("DD") });
      if (ph.indexOf("YYYY") !== -1) order.push({ t: "Y", i: ph.indexOf("YYYY") });
      order.sort((a, b) => a.i - b.i);

      // Extract numeric fields from user input.
      const nums = s.split(/[^\d]/).filter(Boolean);
      if (nums.length < 3) return "";

      let mm = "", dd = "", yyyy = "";
      const a = nums[0], b = nums[1], c = nums[2];

      const seq = order.map(x => x.t).join("");
      if (seq === "MDY") { mm = a; dd = b; yyyy = c; }
      else if (seq === "DMY") { dd = a; mm = b; yyyy = c; }
      else if (seq === "YMD") { yyyy = a; mm = b; dd = c; }
      else {
        // Fallback: assume MDY
        mm = a; dd = b; yyyy = c;
      }

      const m = Number(mm);
      const d = Number(dd);
      const y = Number(yyyy);

      if (!y || y < 1900 || y > 3000) return "";
      if (!m || m < 1 || m > 12) return "";
      if (!d || d < 1 || d > 31) return "";

      const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const check = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(check.getTime())) return "";
      // Validate round-trip (catches invalid dates like 02/31).
      const y2 = check.getFullYear();
      const m2 = check.getMonth() + 1;
      const d2 = check.getDate();
      if (y2 != y || m2 != m || d2 != d) return "";
      return iso;
    } catch {
      return "";
    }
  };


const handleOpenEditCosts = (r, skipUrlPush) => {
    setEditCostsLoadError("");
    setEditCostsRequest(r);
    // Pre-fill from existing final costs if present; otherwise use estimate values if possible.
    const lines = (Array.isArray(r.final_cost_breakdown) && r.final_cost_breakdown.length)
      ? r.final_cost_breakdown
      : ((Array.isArray(r.cost_breakdown) && r.cost_breakdown.length) ? r.cost_breakdown : []);

    const extracted = getCostsFromBreakdown(lines);

    // Keep as strings for <input/> values; normalize to 2 decimals.
    setEditCostsAI(extracted.ai !== null && extracted.ai !== undefined ? formatMoneyInput(String(extracted.ai)) : "");
    setEditCostsLR(extracted.review !== null && extracted.review !== undefined ? formatMoneyInput(String(extracted.review)) : "");
    setEditCostsFmt(extracted.fmt !== null && extracted.fmt !== undefined ? formatMoneyInput(String(extracted.fmt)) : "");

    // Date: ISO YYYY-MM-DD
    const dt = String(r.estimated_completion_date || "");
        const norm = normalizeDateInputValue(dt);
    setEditCostsDate(norm);
    setEditCostsDateText(isoToLocaleDateString(norm));


    setEditCostsModalOpen(true);
    if (!skipUrlPush) syncAdminUrl("requests", r && r.task_id ? r.task_id : "", false);
  };

  const handleCloseEditCosts = (replaceUrl) => {
    setEditCostsModalOpen(false);
    setEditCostsRequest(null);
    setEditCostsAI("");
    setEditCostsLR("");
    setEditCostsFmt("");
        setEditCostsDate("");
    // Keep URL in sync so refresh returns to the correct view
    syncAdminUrl("requests", "", !!replaceUrl);
  };


  const buildProjectRequestDeleteLabel = (r) => {
    try {
      const ts = r?.created_at || "";
      let datePart = "";
      let timePart = "";
      if (ts) {
        const d = new Date(ts);
        if (!Number.isNaN(d.getTime())) {
          datePart = d.toLocaleDateString();
          timePart = d.toLocaleTimeString();
        } else {
          datePart = ts.indexOf("T") !== -1 ? ts.split("T")[0] : ts.split(" ")[0];
          timePart = ts.indexOf("T") !== -1 ? (ts.split("T")[1] || "") : (ts.split(" ")[1] || "");
          timePart = String(timePart).replace("Z", "").split(".")[0];
        }
      }

      const files = Array.isArray(r?.source_filenames) ? r.source_filenames.filter(Boolean) : [];
      const langs = Array.isArray(r?.languages) ? r.languages.filter(Boolean) : [];

      const filePart = files.length <= 1 ? (files[0] || "1 File") : `${files.length} Files`;
      let langPart = "";
      if (langs.length <= 1) {
        langPart = langs[0] || "1 Language";
      } else {
        // If multiple pairs, keep it compact for the modal confirmation text
        langPart = `${langs.length} Languages`;
      }

      const stamp = [datePart, timePart].filter(Boolean).join(", ");
      return `Delete project request ${stamp}${stamp ? ", " : ""}${filePart}, ${langPart}?`;
    } catch {
      return "Delete this project request?";
    }
  };

  const handleDeleteProjectRequest = async (r) => {
    if (!r?.task_id) return;

    const label = buildProjectRequestDeleteLabel(r);
    const ok = window.confirm(label);
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE}/admin/project-requests/${encodeURIComponent(r.task_id)}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      // Remove from list immediately
      setProjectRequests((prev) => (Array.isArray(prev) ? prev.filter((x) => x?.task_id !== r.task_id) : prev));

      // If we were editing this request, close the edit view
      if (editCostsRequest?.task_id && editCostsRequest.task_id === r.task_id) {
        setEditCostsRequest(null);
        setEditCostsLoadError("");
        setEditCostsSavedMsg("");
      }
    } catch (e) {
      console.error("Delete project request failed:", e);
      alert("Failed to delete project request. Please try again.");
    }
  };

  const handleSaveEditCosts = async () => {
    if (!editCostsRequest) return;
    setEditCostsSaving(true);
    try {
      // Update the row immediately so the table reflects the new status without waiting for a refetch
      const aiNum = parseMoneyNumber(editCostsAI);
      const lrNum = parseMoneyNumber(editCostsLR);
      const fmtNum = parseMoneyNumber(editCostsFmt);
      const totalNum = aiNum + lrNum + fmtNum;
      const breakdownLines = [
        `• AI Translation: $${formatMoneyUSD(aiNum)}`,
        `• Estimated Limited Review: $${formatMoneyUSD(lrNum)}`,
        `• Estimated Post-Translation Formatting/QC: $${formatMoneyUSD(fmtNum)}`,
        `Total: $${formatMoneyUSD(totalNum)}`,
      ];

      // Persist to backend (source of truth)
      const saveRes = await axios.post(
        `${API_BASE}/admin/project-requests/${encodeURIComponent(editCostsRequest.task_id)}/quote`,
        {
          ai_translation_usd: aiNum,
          limited_review_usd: lrNum,
          formatting_qc_usd: fmtNum,
          estimated_completion_date: editCostsDate || null,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (!saveRes || !saveRes.data || saveRes.data.ok !== true) {
        throw new Error("Save failed");
      }

      // Update local row immediately from the server response
      updateProjectRequestRow(editCostsRequest.task_id, {
        status: saveRes.data.status || editCostsRequest.status,
        cost_breakdown: saveRes.data.final_cost_breakdown || breakdownLines,
        final_cost_breakdown: saveRes.data.final_cost_breakdown || breakdownLines,
        estimated_completion_date:
          (saveRes.data && saveRes.data.estimated_completion_date !== undefined && saveRes.data.estimated_completion_date !== null)
            ? saveRes.data.estimated_completion_date
            : (editCostsDate || null),
      });

      // Ensure edit screen reflects persisted values
      setEditCostsRequest((prev) => ({
        ...(prev || {}),
        ...(saveRes.data || {}),
        // Keep these fields stable if the save response does not include them.
        estimated_completion_date:
          (saveRes.data && saveRes.data.estimated_completion_date !== undefined)
            ? saveRes.data.estimated_completion_date
            : (editCostsDate || (prev ? prev.estimated_completion_date : null) || null),
        submitted_to_user_at:
          (saveRes.data && saveRes.data.submitted_to_user_at !== undefined)
            ? saveRes.data.submitted_to_user_at
            : (prev ? prev.submitted_to_user_at : undefined),
      }));
      setEditCostsLoadError("");
      // Keep the current input value after Save so the date does not disappear.
      try {
        const norm = normalizeDateInputValue(editCostsDate || "");
        setEditCostsDate(norm);
        setEditCostsDateText(isoToLocaleDateString(norm));
      } catch {
        // ignore
      }


      // Refresh list (server is source of truth)
      setRefreshFlag((f) => f + 1);
      setMessage("Saved.");
    } catch (e) {
      // Keep modal open; show generic message
      setMessage("Failed to save costs.");
    } finally {
      setEditCostsSaving(false);
    }
  };

  const handleSubmitCostsToUser = async () => {
    if (!editCostsRequest) return;
    setEditCostsSubmitting(true);
    try {
      // First, persist any edits (same behavior as Save).
      const aiNum = parseMoneyNumber(editCostsAI);
      const lrNum = parseMoneyNumber(editCostsLR);
      const fmtNum = parseMoneyNumber(editCostsFmt);
      const totalNum = aiNum + lrNum + fmtNum;
      const breakdownLines = [
        `• AI Translation: $${formatMoneyUSD(aiNum)}`,
        `• Estimated Limited Review: $${formatMoneyUSD(lrNum)}`,
        `• Estimated Post-Translation Formatting/QC: $${formatMoneyUSD(fmtNum)}`,
        `Total: $${formatMoneyUSD(totalNum)}`,
      ];

      const saveRes = await axios.post(
        `${API_BASE}/admin/project-requests/${encodeURIComponent(editCostsRequest.task_id)}/quote`,
        {
          ai_translation_usd: aiNum,
          limited_review_usd: lrNum,
          formatting_qc_usd: fmtNum,
          estimated_completion_date: editCostsDate || null,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (!saveRes || !saveRes.data || saveRes.data.ok !== true) {
        throw new Error("Save before submit failed");
      }

      // Update local row immediately from the server response (same as Save).
      updateProjectRequestRow(editCostsRequest.task_id, {
        status: saveRes.data.status || editCostsRequest.status,
        cost_breakdown: saveRes.data.final_cost_breakdown || breakdownLines,
        final_cost_breakdown: saveRes.data.final_cost_breakdown || breakdownLines,
        estimated_completion_date:
          (saveRes.data && saveRes.data.estimated_completion_date !== undefined && saveRes.data.estimated_completion_date !== null)
            ? saveRes.data.estimated_completion_date
            : (editCostsDate || null),
      });

      // Keep the edit screen stable after Save.
      setEditCostsRequest((prev) => ({
        ...(prev || {}),
        ...(saveRes.data || {}),
        estimated_completion_date:
          (saveRes.data && saveRes.data.estimated_completion_date !== undefined)
            ? saveRes.data.estimated_completion_date
            : (editCostsDate || (prev ? prev.estimated_completion_date : null) || null),
        submitted_to_user_at:
          (saveRes.data && saveRes.data.submitted_to_user_at !== undefined)
            ? saveRes.data.submitted_to_user_at
            : (prev ? prev.submitted_to_user_at : undefined),
      }));

      try {
        const norm = normalizeDateInputValue(editCostsDate || "");
        setEditCostsDate(norm);
        setEditCostsDateText(isoToLocaleDateString(norm));
      } catch {
        // ignore
      }

      // Then, submit costs to the user (sends email + updates submitted timestamp).
      const res = await axios.post(
        `${API_BASE}/admin/project-requests/${encodeURIComponent(String(editCostsRequest.task_id))}/submit`,
        {},
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      const nowIso = new Date().toISOString();
      const submittedAt =
        res?.data?.submitted_to_user_at ||
        res?.data?.submitted_at ||
        nowIso;

      const nextStatus = res?.data?.status || "Cost Submitted";

      // Update the edit screen immediately so the submitted timestamp reflects the most recent submit.
      setEditCostsRequest((prev) => ({
        ...(prev || {}),
        status: nextStatus,
        submitted_to_user_at: submittedAt,
      }));

      // Update the table row immediately.
      updateProjectRequestRow(editCostsRequest.task_id, {
        status: nextStatus,
        submitted_to_user_at: submittedAt,
      });

      // Refresh list (server is source of truth).
      setRefreshFlag((f) => f + 1);

      setMessage("Costs submitted to user.");
    } catch (e) {
      setMessage("Failed to submit costs to user.");
    } finally {
      setEditCostsSubmitting(false);
    }
  };
  const handleSetCredits = (u) => {
    const amount = creditsToSet[u.id];
    if (amount === "" || isNaN(amount)) return;
    axios.post(`${API_BASE}/admin/set-credits`, {
      email: u.email,
      amount: Number(amount)
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Set credits for ${u.email} to ${amount}`);
        setCreditsToSet(prev => ({ ...prev, [u.id]: "" }));
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to set credits"));
  };

  const handleDeleteUser = (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    axios.delete(`${API_BASE}/admin/delete-user/${u.id}`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Deleted user ${u.email}`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to delete user"));
  };

  const normalizeTimestampForLocalDisplay = (ts) => {
    try {
      const s0 = String(ts || "").trim();
      if (!s0) return "";

      // If the backend returns a naive timestamp (no Z / no offset), treat it as UTC.
      const hasTz = /[zZ]|[\+\-]\d{2}:?\d{2}$/.test(s0);
      if (hasTz) return s0;

      // YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD HH:MM:SS
      if (/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(:\d{2})?/.test(s0)) {
        const isoLike = s0.indexOf(" ") !== -1 ? s0.replace(" ", "T") : s0;
        return `${isoLike}Z`;
      }

      return s0;
    } catch {
      return String(ts || "");
    }
  };

  const formatProjectRequestTimestamp = (ts) => {
    if (!ts) return "";
    try {
      const dt = new Date(ts);
      if (isNaN(dt.getTime())) return String(ts || "");
      return dt.toLocaleString();
    } catch (e) {
      return String(ts || "");
    }
  };

  const getApprovalsEstimateCode = (r) => {
    try {
      const src = String(
        r?.bundle_zip_url ||
          r?.translated_zip_filename ||
          r?.filename ||
          r?.translated_zip_url ||
          ""
      );
      const mm = src.match(/(\d{8}-\d{6})/);
      return mm ? mm[1] : "";
    } catch {
      return "";
    }
  };

  const mapApprovalsStatusLabel = (statusRaw) => {
    const s = String(statusRaw || "").trim();
    if (s === "Sent to Ops") return "Quote Approved";
    return s || "-";
  };

  const toggleApprovalsSort = (key) => {
    if (!key) return;

    const nextKey = String(key);

    if (String(approvalsSortKey) === nextKey) {
      setApprovalsSortDir((currentDirection) =>
        currentDirection === "asc" ? "desc" : "asc"
      );
      return;
    }

    setApprovalsSortKey(nextKey);
    setApprovalsSortDir(nextKey === "date" ? "desc" : "asc");
  };

  const approvalsSortIndicator = (key) => {
    if (String(approvalsSortKey) !== String(key)) return " ↕";
    return approvalsSortDir === "asc" ? " ▲" : " ▼";
  };

  const toggleDraftsSort = (key) => {
    if (!key) return;
    setDraftsSortKey(prev => {
      if (prev === key) {
        setDraftsSortDir(d => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      // Default Updated sort is desc, others asc (same behavior as other tables)
      setDraftsSortDir(key === "updated" ? "desc" : "asc");
      return key;
    });
  };

  const draftsSortIndicator = (key) => {
    if (!key) return "";
    if (String(draftsSortKey) !== String(key)) return " ↕";
    return draftsSortDir === "asc" ? " ▲" : " ▼";
  };

  const sortedDraftUsers = useMemo(() => {
    const arr = Array.isArray(drafts) ? [...drafts] : [];
    const dirMul = draftsSortDir === "asc" ? 1 : -1;

    const getUpdatedTs = (d) => {
      const v = d?.updated_at || d?.updated || "";
      const dt = new Date(v);
      const ms = dt instanceof Date && !isNaN(dt.getTime()) ? dt.getTime() : 0;
      return ms;
    };

    arr.sort((a, b) => {
      let cmp = 0;

      if (draftsSortKey === "email") {
        const ea = String(a?.email || "").toLowerCase();
        const eb = String(b?.email || "").toLowerCase();
        cmp = ea.localeCompare(eb);
      } else if (draftsSortKey === "name") {
        const na = String((a?.first_name || "") + " " + (a?.last_name || "")).trim().toLowerCase();
        const nb = String((b?.first_name || "") + " " + (b?.last_name || "")).trim().toLowerCase();
        cmp = na.localeCompare(nb);
      } else if (draftsSortKey === "company") {
        const ca = String(a?.company || "").toLowerCase();
        const cb = String(b?.company || "").toLowerCase();
        cmp = ca.localeCompare(cb);
      } else if (draftsSortKey === "step") {
        const sa = Number(a?.step || 0);
        const sb = Number(b?.step || 0);
        cmp = sa - sb;
      } else if (draftsSortKey === "status") {
        const sa = String(a?.contact_stage || a?.status || "").toLowerCase();
        const sb = String(b?.contact_stage || b?.status || "").toLowerCase();
        cmp = sa.localeCompare(sb);
      } else {
        // "updated" (default)
        cmp = getUpdatedTs(a) - getUpdatedTs(b);
      }

      return cmp * dirMul;
    });

    return arr;
  }, [drafts, draftsSortKey, draftsSortDir]);


  const sortedProjectRequests = useMemo(() => {
    const arr = Array.isArray(projectRequests) ? [...projectRequests] : [];
    const dirMul = approvalsSortDir === "asc" ? 1 : -1;

    const safeStr = (v) => String(v ?? "").toLowerCase();
    const safeTime = (v) => {
      try {
        const d = new Date(normalizeTimestampForLocalDisplay(v));
        const t = d.getTime();
        return Number.isNaN(t) ? 0 : t;
      } catch {
        return 0;
      }
    };

    arr.sort((a0, b0) => {
      const a = a0 || {};
      const b = b0 || {};

      if (approvalsSortKey === "estimate") {
        const av = safeStr(getApprovalsEstimateCode(a));
        const bv = safeStr(getApprovalsEstimateCode(b));
        return av.localeCompare(bv) * dirMul;
      }

      if (approvalsSortKey === "user") {
        const aName = [String(a?.user_last_name || "").trim(), String(a?.user_first_name || "").trim()]
          .filter(Boolean)
          .join(", ") || String(a?.user_email || "").trim() || "";
        const bName = [String(b?.user_last_name || "").trim(), String(b?.user_first_name || "").trim()]
          .filter(Boolean)
          .join(", ") || String(b?.user_email || "").trim() || "";
        return safeStr(aName).localeCompare(safeStr(bName)) * dirMul;
      }

      if (approvalsSortKey === "status") {
        const av = safeStr(mapApprovalsStatusLabel(a.status));
        const bv = safeStr(mapApprovalsStatusLabel(b.status));
        return av.localeCompare(bv) * dirMul;
      }

      // default: date
      const av = safeTime(a.created_at);
      const bv = safeTime(b.created_at);
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });

    return arr;
  }, [projectRequests, approvalsSortKey, approvalsSortDir]);

const handleDownloadProjectRequest = async (r) => {
  const taskId = r?.task_id;
  if (!taskId) {
    setMessage("Failed to download (missing task_id).");
    return;
  }

  try {
    const resp = await axios.get(
      `${API_BASE}/admin/project-requests/${encodeURIComponent(taskId)}/download`,
      { headers: { Authorization: `Bearer ${jwt}` } }
    );

    const url = resp?.data?.url;
    if (!url) {
      setMessage("No bundled download is available for this request.");
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  } catch (e) {
    setMessage("Failed to generate download link.");
  }

  };



  const handleCreateUser = () => {
    if (!newUserEmail || !newUserFirstName || !newUserLastName) return;
    axios.post(`${API_BASE}/admin/create-user`, {
      email: newUserEmail,
      first_name: newUserFirstName,
      last_name: newUserLastName,
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Created user ${newUserEmail}`);
        setNewUserEmail("");
        setNewUserFirstName("");
        setNewUserLastName("");
        setRefreshFlag(f => f + 1);
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        if (detail && detail.toLowerCase().includes("already registered")) {
          setMessage("User already exists.");
        } else {
          setMessage("Failed to create user.");
        }
      });
  };

  const handleMakeAdmin = (u) => {
    if (!window.confirm(`Make ${u.email} an Admin?`)) return;
    axios.post(`${API_BASE}/admin/make-admin`, {
      user_id: u.id
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`User ${u.email} is now an Admin.`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to make user Admin."));
  };

  const handleRemoveAdmin = (u) => {
    if (!window.confirm(`Remove admin status from ${u.email}?`)) return;
    axios.post(`${API_BASE}/admin/remove-admin`, {
      user_id: u.id
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`User ${u.email} is now a regular User.`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to remove admin status."));
  };


  const handleMakeAssistant = (u) => {
    if (!window.confirm(`Make ${u.email} an Assistant?`)) return;
    axios.post(`${API_BASE}/admin/make-assistant`, {
      user_id: u.id
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`User ${u.email} is now an Assistant.`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to make user Assistant."));
  };

  const handleRemoveAssistant = (u) => {
    if (!window.confirm(`Remove Assistant role from ${u.email}?`)) return;
    axios.post(`${API_BASE}/admin/remove-assistant`, {
      user_id: u.id
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`User ${u.email} is no longer an Assistant.`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to remove Assistant role."));
  };


  const canEdit = (targetUser) => {
    if (!user) return false;
    const isSelf = user.id === targetUser.id;
    const isUserSuper = Boolean(user.is_super_admin);
    const isUserAdmin = Boolean(user.is_admin);
    const isTargetSuper = Boolean(targetUser.is_super_admin);
    const isTargetAdmin = Boolean(targetUser.is_admin);

    // Super Admin: can edit anyone except other super admins
    if (isUserSuper) return !isTargetSuper || isSelf;
    // Admin: can edit self, or users (not admins/superadmins)
    else if (isUserAdmin) return isSelf || (!isTargetAdmin && !isTargetSuper);
    // Users: only self
    return isSelf;
  };

  const handleEditFieldChange = (u, field, value) => {
    setEditFields(prev => ({
      ...prev,
      [u.id]: { ...(prev[u.id] || {}), [field]: value }
    }));
  };

  const handleEdit = (u) => {
    setEditingId(u.id);
    setEditFields(prev => ({
      ...prev,
      [u.id]: {
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
      }
    }));
  };

  const handleSaveUser = (u) => {
    const edit = editFields[u.id];
    const changed =
      edit &&
      (edit.first_name !== u.first_name ||
        edit.last_name !== u.last_name ||
        edit.email !== u.email);

    setEditFields(prev => {
      const { [u.id]: _, ...rest } = prev;
      return rest;
    });
    setEditingId(null);

    if (!changed) {
      return;
    }

    axios.post(`${API_BASE}/admin/update-user`, {
      id: u.id,
      first_name: edit.first_name ?? u.first_name,
      last_name: edit.last_name ?? u.last_name,
      email: edit.email ?? u.email
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Updated user ${edit.email ?? u.email}`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to update user"));
  };

  const toggleUsersSort = (key) => {
    if (!key) return;
    setUsersSortKey(prev => {
      if (prev === key) {
        setUsersSortDir(d => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setUsersSortDir("asc");
      return key;
    });
  };

  const usersSortIndicator = (key) => {
    if (!key) return "";
    if (usersSortKey === key) return usersSortDir === "asc" ? " ▲" : " ▼";
    return " ↕";
  };

  let sortedUsers = [...users];
  sortedUsers.sort((a, b) => {
    // Keep the current logged-in user at the top for convenience
    if (user && a.id === user.id) return -1;
    if (user && b.id === user.id) return 1;

    const dir = usersSortDir === "asc" ? 1 : -1;

    const lastA = (a.last_name || "").toLowerCase();
    const lastB = (b.last_name || "").toLowerCase();
    const firstA = (a.first_name || "").toLowerCase();
    const firstB = (b.first_name || "").toLowerCase();
    const emailA = (a.email || "").toLowerCase();
    const emailB = (b.email || "").toLowerCase();

    const typeLabel = (u) => {
      if (u.is_super_admin) return "super admin";
      if (u.is_admin) return "admin";
      if (u.is_assistant) return "assistant";
      return "user";
    };

    let cmp = 0;

    if (usersSortKey === "first") {
      cmp = firstA.localeCompare(firstB) || lastA.localeCompare(lastB) || emailA.localeCompare(emailB);
    } else if (usersSortKey === "email") {
      cmp = emailA.localeCompare(emailB);
    } else if (usersSortKey === "type") {
      cmp = typeLabel(a).localeCompare(typeLabel(b)) || lastA.localeCompare(lastB) || firstA.localeCompare(firstB) || emailA.localeCompare(emailB);
    } else if (usersSortKey === "confirmed") {
      const ca = a.is_confirmed ? 1 : 0;
      const cb = b.is_confirmed ? 1 : 0;
      cmp = ca - cb;
    } else {
      // "last" (default)
      cmp = lastA.localeCompare(lastB) || firstA.localeCompare(firstB) || emailA.localeCompare(emailB);
    }

    return cmp * dir;
  });


  // ---- SEARCH LOGIC ----
  const filteredUsers = search.trim()
    ? sortedUsers.filter(u => {
        const s = search.trim().toLowerCase();
        return (
          (u.first_name || "").toLowerCase().includes(s) ||
          (u.last_name || "").toLowerCase().includes(s) ||
          (u.email || "").toLowerCase().includes(s)
        );
      })
    : sortedUsers;
  // ----------------------

  // -------- HIDE LOGIC FOR BUTTONS -------
  const showSetCredits = (u) => {
    // Only show if:
    // - Viewing own row (Admins always see own row, even if admin)
    // - User is not a super admin (for anyone else)
    // - User is admin viewing a user (not admin/superadmin)
    if (user.id === u.id) return true;
    if (user.is_super_admin) {
      if (u.is_super_admin) return false;
      return true;
    }
    if (user.is_admin) {
      if (u.is_super_admin || u.is_admin) return false;
      return true;
    }
    return false;
  };

  const showEdit = (u) => {
    if (user.id === u.id) return true;
    if (user.is_super_admin) {
      if (u.is_super_admin) return false;
      return true;
    }
    if (user.is_admin) {
      if (u.is_super_admin || u.is_admin) return false;
      return true;
    }
    return false;
  };

  const showDeleteUser = (u) => {
    // Only allow if:
    // - Super Admin deleting non-super-admin and not self
    // - Admin deleting user (not self, not admins, not super admins)
    if (user.is_super_admin) {
      if (u.is_super_admin) return false;
      if (user.id === u.id) return false;
      return true;
    }
    if (user.is_admin) {
      if (u.is_super_admin || u.is_admin) return false;
      if (user.id === u.id) return false;
      return true;
    }
    return false;
  };
  
  const handleDraftSendEmail = (d, template) => {
    axios.post(`${API_BASE}/admin/workflow/drafts/${d.id}/send-email`, { template }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Email sent to ${d.email} (${template}).`);
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to send e-mail."));
  };

  const handleDraftConvert = (d) => {
    if (!window.confirm(`Create a user for ${d.email}?`)) return;
    axios.post(`${API_BASE}/admin/workflow/drafts/${d.id}/convert`, {}, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage(`Draft converted: ${d.email}`);
        setRefreshFlag(f => f + 1);
      })
      .catch(err => {
        const detail = err.response?.data?.detail || "Failed to convert draft.";
        setMessage(detail);
      });
  };

  const handleDraftDelete = (d) => {
    if (!window.confirm(`Delete draft ${d.email || "(no email)"}?`)) return;
    axios.delete(`${API_BASE}/admin/workflow/drafts/${d.id}`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => {
        setMessage("Draft deleted.");
        setRefreshFlag(f => f + 1);
      })
      .catch(() => setMessage("Failed to delete draft."));
  };
  
  const handleDraftDownloadTerminology = (d) => {
    axios
      .get(`${API_BASE}/admin/workflow/drafts/${d.id}/terminology-download`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(res => {
        const url = res.data?.url;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          setMessage("No terminology file available for this draft.");
        }
      })
      .catch(() => {
        setMessage("Failed to download terminology file.");
      });
  };

  const handleDraftTMXClick = async (d) => {
    if (!jwt) {
      window.alert("You must be logged in to view TMX files.");
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/admin/workflow/drafts/${d.id}/tmx`,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );

      const items = Array.isArray(res.data) ? res.data : [];

      if (items.length === 0) {
        window.alert("This draft has no TMX entries.");
        return;
      }

      setDraftTmxModalDraft(d);
      setDraftTmxModalItems(items);
    } catch (err) {
      console.error("Failed to load draft TMX:", err);
      window.alert("Failed to load TMX for this draft.");
    }
  };

  // Per-entry TMX download (Draft Users)
  const handleDraftDownloadTMX = (d, item) => {
    axios
      .get(`${API_BASE}/admin/workflow/drafts/${d.id}/tmx-download/${item.id}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => {
        const url = res.data?.url;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          setMessage("No TMX file available for this entry.");
        }
      })
      .catch(() => {
        setMessage("Failed to download TMX file for this entry.");
      });
  };

  // Per-entry Terminology download (Draft Users)
  const handleUserTerminologyClick = async (u) => {
    if (!jwt) {
      window.alert("You must be logged in to view terminology files.");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/admin/terminology/${u.id}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      const items = Array.isArray(res.data) ? res.data : [];

      if (items.length === 0) {
        window.alert("This user has no terminology entries.");
        return;
      }

      // Open modal and show all entries with download buttons
      setTermModalUser(u);
      setTermModalItems(items);
    } catch (err) {
      console.error("Failed to load user terminology:", err);
      window.alert("Failed to load terminology for this user.");
    }
  };

  const downloadUserTerminologyFile = async (userId, term) => {
    if (!jwt) return;

    if (!term.s3_key) {
      window.alert(
        "This terminology entry does not have a stored file (prompt-only)."
      );
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/admin/terminology/${userId}/${term.id}/download`,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );

      const url = res.data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.alert(
          "Unable to get a download link for this terminology file."
        );
      }
    } catch (err) {
      console.error("Failed to download terminology file:", err);
      window.alert("Failed to download terminology file.");
    }
  };

  const handleUserTMXClick = async (u) => {
    if (!jwt) {
      window.alert("You must be logged in to view TMX files.");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/admin/tmx/${u.id}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      const items = Array.isArray(res.data) ? res.data : [];

      if (items.length === 0) {
        window.alert("This user has no TMX entries.");
        return;
      }

      setTmxModalUser(u);
      setTmxModalItems(items);
    } catch (err) {
      console.error("Failed to load user TMX:", err);
      window.alert("Failed to load TMX for this user.");
    }
  };

  const downloadUserTMXFile = async (userId, tmx) => {
    if (!jwt) return;

    if (!tmx.s3_key) {
      window.alert(
        "This TMX entry does not have a stored file."
      );
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/admin/tmx/${userId}/${tmx.id}/download`,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );

      const url = res.data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.alert("Unable to get a download link for this TMX file.");
      }
    } catch (err) {
      console.error("Failed to download TMX file:", err);
      window.alert("Failed to download TMX file.");
    }
  };

  
  // --------------------------------------

  const abuseSearchLower = abuseSearch.trim().toLowerCase();
  const matchesAbuseSearch = (...values) =>
    !abuseSearchLower ||
    values.some((value) =>
      String(value || "").toLowerCase().includes(abuseSearchLower)
    );

  const combinedBlockedEmails = [
    ...(abuseData.blocked_emails || []).map((row) => ({ ...row, block_type: "E-mail", block_value: row.email })),
    ...(abuseData.blocked_email_domains || []).map((row) => ({ ...row, block_type: "Domain", block_value: row.domain, is_domain_block: true })),
  ];
  const blockedEmailSearchLower = blockedEmailSearch.trim().toLowerCase();
  const filteredSuppressedEmails = combinedBlockedEmails.filter((row) => {
    const matchesGlobal = matchesAbuseSearch(row.block_value, row.reason, row.mailtrap_event, row.source, row.block_type);
    const matchesBlockedList = !blockedEmailSearchLower || [
      row.block_value,
      row.reason,
      row.mailtrap_event,
      row.source,
      row.block_type,
    ].some((value) => String(value || "").toLowerCase().includes(blockedEmailSearchLower));
    return matchesGlobal && matchesBlockedList;
  });
  const visibleSuppressedEmails = blockedEmailDisplaySize === "all"
    ? filteredSuppressedEmails
    : filteredSuppressedEmails.slice(0, Number(blockedEmailDisplaySize) || 50);
  const filteredBlockedIPs = (abuseData.blocked_ips || []).filter((row) =>
    matchesAbuseSearch(row.ip_address, row.reason)
  );
  const filteredAbuseEvents = (abuseData.events || []).filter((row) =>
    matchesAbuseSearch(
      row.event_type,
      row.ip_address,
      row.email,
      row.endpoint,
      row.detail
    )
  );
  const filteredWebhookHistory = (abuseData.webhook_history || []).filter((row) =>
    matchesAbuseSearch(row.event_type, row.email, row.event_id)
  );
  const filteredThreatFeeds = (abuseData.threat_intel?.feeds || []).filter((row) =>
    matchesAbuseSearch(row.source, row.last_error, row.item_count)
  );
  const filteredThreatAllowlist = (abuseData.threat_intel?.allowlist || []).filter((row) =>
    matchesAbuseSearch(row.indicator_type, row.indicator, row.reason)
  );

  const handleRemoveEmailBlock = async (row) => {
    if (!row?.id) return;
    const value = row.block_value || row.email || row.domain || "this entry";
    const ok = window.confirm(
      `Remove the block for ${value}? This will allow future use of this address/domain again.`
    );
    if (!ok) return;

    try {
      const path = row.is_domain_block
        ? `/admin/blocked-email-domains/${encodeURIComponent(String(row.id))}`
        : `/admin/blocked-emails/${encodeURIComponent(String(row.id))}`;
      await axios.delete(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${jwt}` } });
      setMessage(`Block removed for ${value}.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to remove e-mail/domain block.");
    }
  };


  const handleAddBlockedEmail = async () => {
    const email = newBlockedEmail.trim();
    const reason = newBlockedEmailReason.trim();
    if (!email || !reason) {
      setMessage("Enter an e-mail address or domain and a Reason for Block.");
      return;
    }
    setAbuseActionLoading(true);
    try {
      await axios.post(
        `${API_BASE}/admin/blocked-emails`,
        { email, reason },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setNewBlockedEmail("");
      setNewBlockedEmailReason("");
      setMessage(`Blocked ${email}.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to block e-mail address.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleAddBlockedIP = async () => {
    const ipAddress = newBlockedIP.trim();
    const reason = newBlockedIPReason.trim();
    if (!ipAddress || !reason) {
      setMessage("Enter both an IP address and a Reason for Block.");
      return;
    }
    setAbuseActionLoading(true);
    try {
      await axios.post(
        `${API_BASE}/admin/blocked-ips`,
        { ip_address: ipAddress, reason },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setNewBlockedIP("");
      setNewBlockedIPReason("");
      setMessage(`Blocked ${ipAddress}.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to block IP address.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleImportBlockedEmails = async () => {
    if (!blockedEmailImportFile) {
      setMessage("Choose a TXT, CSV, or XLSX file to import.");
      return;
    }
    setAbuseActionLoading(true);
    try {
      const form = new FormData();
      form.append("file", blockedEmailImportFile);
      form.append("default_reason", blockedEmailImportReason.trim() || "Imported by administrator");
      const res = await axios.post(`${API_BASE}/admin/blocked-emails/import`, form, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const imported = Number(res?.data?.imported || 0);
      const skipped = Number(res?.data?.skipped || 0);
      setMessage(`Imported ${imported} blocked e-mail/domain entr${imported === 1 ? "y" : "ies"}${skipped ? `; skipped ${skipped}` : ""}.`);
      setBlockedEmailImportFile(null);
      if (blockedEmailImportRef.current) blockedEmailImportRef.current.value = "";
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to import blocked e-mails.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleImportBlockedIPs = async () => {
    if (!blockedIPImportFile) {
      setMessage("Choose a TXT, CSV, or XLSX file to import.");
      return;
    }
    setAbuseActionLoading(true);
    try {
      const form = new FormData();
      form.append("file", blockedIPImportFile);
      form.append("default_reason", blockedIPImportReason.trim() || "Imported by administrator");
      const res = await axios.post(`${API_BASE}/admin/blocked-ips/import`, form, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const imported = Number(res?.data?.imported || 0);
      const skipped = Number(res?.data?.skipped || 0);
      setMessage(`Imported ${imported} blocked IP${imported === 1 ? "" : "s"}${skipped ? `; skipped ${skipped}` : ""}.`);
      setBlockedIPImportFile(null);
      if (blockedIPImportRef.current) blockedIPImportRef.current.value = "";
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to import blocked IPs.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleRemoveIPBlock = async (row) => {
    if (!row?.id) return;
    const ok = window.confirm(
      `Remove the block for ${row.ip_address}? This IP will be allowed to submit forms again.`
    );
    if (!ok) return;

    try {
      await axios.delete(
        `${API_BASE}/admin/blocked-ips/${encodeURIComponent(String(row.id))}`,
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setMessage(`IP block removed for ${row.ip_address}.`);
      setRefreshFlag((f) => f + 1);
    } catch {
      setMessage("Failed to remove IP block.");
    }
  };

  const handleRefreshThreatIntel = async () => {
    setAbuseActionLoading(true);
    setMessage("Refreshing threat-intelligence feeds...");
    try {
      const res = await axios.post(`${API_BASE}/admin/threat-intel/refresh`, {}, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const results = res?.data?.results || {};
      const summary = Object.entries(results).map(([source, info]) => `${source}: ${info?.status || "unknown"}${info?.items !== undefined ? ` (${Number(info.items).toLocaleString()})` : ""}`).join("; ");
      setMessage(`Threat-intelligence refresh complete${summary ? ` — ${summary}` : ""}.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to refresh threat-intelligence feeds.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleAddThreatAllowlist = async () => {
    const indicator = threatAllowValue.trim();
    if (!indicator) {
      setMessage("Enter an IP address, IP range, or domain to allowlist.");
      return;
    }
    setAbuseActionLoading(true);
    try {
      await axios.post(`${API_BASE}/admin/threat-intel/allowlist`, {
        indicator_type: threatAllowType,
        indicator,
        reason: threatAllowReason.trim() || "Allowlisted by administrator",
      }, { headers: { Authorization: `Bearer ${jwt}` } });
      setThreatAllowValue("");
      setThreatAllowReason("");
      setMessage(`Allowlisted ${indicator} for threat-intelligence checks.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to add threat-intelligence allowlist entry.");
    } finally {
      setAbuseActionLoading(false);
    }
  };

  const handleRemoveThreatAllowlist = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Remove ${row.indicator} from the threat-intelligence allowlist?`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/threat-intel/allowlist/${encodeURIComponent(String(row.id))}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setMessage(`Removed ${row.indicator} from the threat-intelligence allowlist.`);
      setRefreshFlag((f) => f + 1);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to remove allowlist entry.");
    }
  };

  const handleAuditExport = async () => {
    if (!jwt) return;
    const params = new URLSearchParams();
    params.set("days", String(auditDays || 0));
    params.set("limit", "5000");
    if (auditSearch.trim()) params.set("q", auditSearch.trim());
    if (auditAction.trim()) params.set("action", auditAction.trim());
    if (auditTargetEmail.trim()) params.set("target_email", auditTargetEmail.trim());

    try {
      const res = await axios.get(`${API_BASE}/admin/audit-log.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${jwt}` },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `admin_audit_log_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setMessage("Failed to export audit log.");
    }
  };

  const handleAuditDetailsDownload = (row) => {
    const detailsText = JSON.stringify(
      {
        id: row?.id || "",
        created_at: row?.created_at || "",
        admin: row?.actor_email || "",
        action: row?.action || "",
        target_type: row?.target_type || "",
        target_id: row?.target_id || "",
        target_email: row?.target_email || "",
        before: row?.before || null,
        after: row?.after || null,
        details: row?.details || null,
      },
      null,
      2
    );

    const blobUrl = window.URL.createObjectURL(
      new Blob([detailsText], { type: "text/plain;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", `admin_history_details_${row?.id || "entry"}.txt`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };


  if (!(user?.is_admin || user?.is_super_admin || user?.is_assistant)) {
    return <div className="p-6 text-center text-lg">You are not an admin.</div>;
  }

  return (
    <div data-testid="admin-page" className="w-full max-w-none mx-auto mt-10 bg-white rounded shadow p-6">
      
      {/* Tabs */}
      <div data-testid="admin-tabs" className="mb-6 border-b pb-2 flex gap-3 flex-wrap">
        {!isAssistantOnly && (
          <>
        <button
          data-testid="admin-users-tab"
          className={`px-3 py-1 rounded-t ${tab === "users" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
          style={{ backgroundColor: tab === "users" ? "#00a7e1" : undefined }}
          onClick={() => { handleCloseEditCosts(true); setTab("users"); syncAdminUrl("users", "", false); }}
        >
          Users
        </button>

        <button
          data-testid="admin-drafts-tab"
          className={`px-3 py-1 rounded-t ${tab === "drafts" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
          style={{ backgroundColor: tab === "drafts" ? "#00a7e1" : undefined }}
          onClick={() => { handleCloseEditCosts(true); setTab("drafts"); syncAdminUrl("drafts", "", false); }}
        >
          Draft Users
        </button>

          </>
        )}
        <button
          data-testid="admin-project-approvals-tab"
          className={`px-3 py-1 rounded-t ${tab === "requests" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
          style={{ backgroundColor: tab === "requests" ? "#00a7e1" : undefined }}
          onClick={() => { handleCloseEditCosts(true); setTab("requests"); syncAdminUrl("requests", "", false); }}
        >Project Approvals</button>

        {!isAssistantOnly && (
          <button
          data-testid="admin-purchase-history-tab"
          className={`px-3 py-1 rounded-t ${tab === "history" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
          style={{ backgroundColor: tab === "history" ? "#00a7e1" : undefined }}
          onClick={() => { handleCloseEditCosts(true); setTab("history"); syncAdminUrl("history", "", false); }}
        >
          Purchase History
        </button>
        )}

        {(isUserAdmin || isUserSuper) && (
          <button
            data-testid="admin-payment-tracking-tab"
            className={`px-3 py-1 rounded-t ${tab === "payments" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
            style={{ backgroundColor: tab === "payments" ? "#00a7e1" : undefined }}
            onClick={() => { handleCloseEditCosts(true); setTab("payments"); syncAdminUrl("payments", "", false); }}
          >
            Payment Errors/Reconciliation
          </button>
        )}

        {(isUserAdmin || isUserSuper) && (
          <button
            data-testid="admin-abuse-protection-tab"
            className={`px-3 py-1 rounded-t ${tab === "abuse" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
            style={{ backgroundColor: tab === "abuse" ? "#00a7e1" : undefined }}
            onClick={() => { handleCloseEditCosts(true); setTab("abuse"); syncAdminUrl("abuse", "", false); }}
          >
            Abuse Protection
          </button>
        )}

        {(isUserAdmin || isUserSuper) && (
          <button
            data-testid="admin-history-tab"
            className={`px-3 py-1 rounded-t ${tab === "audit" ? "text-white hover:opacity-90" : "bg-gray-200"}`}
            style={{ backgroundColor: tab === "audit" ? "#00a7e1" : undefined }}
            onClick={() => { handleCloseEditCosts(true); setTab("audit"); syncAdminUrl("audit", "", false); }}
          >
            Admin History
          </button>
        )}
      </div>

            {/* USERS TAB */}
      {!isAssistantOnly && tab === "users" && (
        <>
          {/* Create User block */}
          <div data-testid="admin-create-user-section" className="mb-6">
            <h3 className="font-semibold mb-2">Create New User (Admin Shortcut)</h3>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                placeholder="First Name"
                className="border p-2 rounded w-32"
                value={newUserFirstName}
                onChange={e => setNewUserFirstName(e.target.value)}
              />
              <input
                type="text"
                placeholder="Last Name"
                className="border p-2 rounded w-32"
                value={newUserLastName}
                onChange={e => setNewUserLastName(e.target.value)}
              />
              <input
                type="email"
                placeholder="Email"
                className="border p-2 rounded w-52"
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
              />
              <button
                className="text-white px-3 py-2 rounded hover:opacity-90"
                style={{ backgroundColor: "#00a7e1" }}
                onClick={handleCreateUser}
              >
                Create
              </button>
            </div>

            

          {/* SEARCH BAR - update to label left and left-aligned */}
          <div className="mb-4 flex flex-col items-start">
            <div className="flex items-center w-full max-w-md">
              <label htmlFor="admin-user-search" className="font-semibold mr-2 whitespace-nowrap">
                Search:
              </label>
              <input
                id="admin-user-search"
                data-testid="admin-users-search"
                type="text"
                placeholder="Search by name or email…"
                className="border p-2 rounded flex-1"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            {search.trim() !== "" && (
              <button
                className="mt-2 underline text-xs hover:opacity-90"
                style={{ color: "#00a7e1" }}
                onClick={() => setSearch("")}
                data-testid="admin-users-search-clear"
              >
                Clear search
              </button>
            )}
          </div>

          <div data-testid="admin-users-section" className="mb-4">
            <h3 className="font-semibold mb-2">All Users</h3>
            <TableScrollContainer>
              <table data-testid="admin-users-table" className="min-w-full border">
                <thead>
                  <tr>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[120px] cursor-pointer select-none" onClick={() => toggleUsersSort("last")}>Last Name{usersSortIndicator("last")}</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[120px] cursor-pointer select-none" onClick={() => toggleUsersSort("first")}>First Name{usersSortIndicator("first")}</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border break-all min-w-[200px] max-w-[250px] cursor-pointer select-none" onClick={() => toggleUsersSort("email")}>E-mail{usersSortIndicator("email")}</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border text-center min-w-[90px]">Credits</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border text-center min-w-[70px] cursor-pointer select-none" onClick={() => toggleUsersSort("type")}>Type{usersSortIndicator("type")}</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border text-center text-[10px] min-w-[65px] cursor-pointer select-none" onClick={() => toggleUsersSort("confirmed")}>Confirmed?{usersSortIndicator("confirmed")}</th>
                    <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[210px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-gray-500 py-6">
                        No users found.
                      </td>
                    </tr>
                  )}
                  {filteredUsers.map((u, idx) => {
                    const isSuper = u.is_super_admin;
                    const isAdmin = u.is_admin;
                    const isSelf = user && user.id === u.id;
                    let typeLabel = "User";
                    if (isSuper) typeLabel = "Super Admin";
                    else if (isAdmin) typeLabel = "Admin";
                    else if (u.is_assistant) typeLabel = "Assistant";
                    const editing = editFields[u.id] || {};
                    const isEditing = editingId === u.id;

                    // "Set Credits" logic
                    const showSet = showSetCredits(u);
                    // "Edit" logic
                    const showEd = showEdit(u);
                    // "Delete User" logic
                    const showDel = showDeleteUser(u);

                    // Super Admin can make a User into an Admin (not self, not Admins/SuperAdmins)
                    const showMakeAdmin =
                      user.is_super_admin && !isAdmin && !isSuper && !isSelf;
                    // Super Admin can remove Admin status from Admins (not themselves or Super Admins)
                    const showRemoveAdmin =
                      user.is_super_admin && isAdmin && !isSuper && !isSelf;

                    // Admins and Super Admins can promote to assistant (not super admins)
                    const canManageAssistants = (user.is_super_admin || user.is_admin) && !isAssistantOnly;
                    const showMakeAssistant = canManageAssistants && !isSuper && !isSelf && !isAdmin && !u.is_assistant;
                    const showRemoveAssistant = canManageAssistants && !isSuper && !isSelf && !isAdmin && !!u.is_assistant;
const allowEdit = canEdit(u);
                    const highlight = isSelf ? "bg-yellow-100 font-semibold" : "";


                    const stripe = (idx % 2 === 0) ? "bg-white" : "bg-gray-50";
                    const rowClass = isSelf ? "bg-yellow-100 font-semibold" : stripe;
                    return (
                      <tr
                        key={u.id}
                        className={rowClass}
                        title={isSuper ? "Super Admin" : ""}
                      >
                        {/* Last Name */}
                        <td className="p-2 text-xs border min-w-[120px]">
                          {allowEdit && isEditing ? (
                            <input
                              type="text"
                              value={editing.last_name !== undefined ? editing.last_name : u.last_name || ""}
                              className="border px-2 py-1 rounded w-28"
                              onChange={e => handleEditFieldChange(u, "last_name", e.target.value)}
                            />
                          ) : (u.last_name || "")}
                        </td>
                        {/* First Name */}
                        <td className="p-2 text-xs border min-w-[120px]">
                          {allowEdit && isEditing ? (
                            <input
                              type="text"
                              value={editing.first_name !== undefined ? editing.first_name : u.first_name || ""}
                              className="border px-2 py-1 rounded w-28"
                              onChange={e => handleEditFieldChange(u, "first_name", e.target.value)}
                            />
                          ) : (u.first_name || "")}
                        </td>
                        {/* E-mail */}
                        <td className="p-2 text-xs border break-all min-w-[200px] max-w-[250px]">
                          {allowEdit && isEditing ? (
                            <input
                              type="email"
                              value={editing.email !== undefined ? editing.email : u.email}
                              className="border px-2 py-1 rounded w-44"
                              onChange={e => handleEditFieldChange(u, "email", e.target.value)}
                            />
                          ) : u.email.split("@").reduce((acc, part, idx) => {
                            if (idx === 0) return [part];
                            return [...acc, <wbr key={idx} />, "@", part];
                          }, [])}
                        </td>
                        {/* Credits */}
                        <td className="p-2 text-xs text-center border min-w-[90px]">
                          {Number(u.credits).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        {/* Type */}
                        <td className="p-2 text-xs text-center border min-w-[70px]">{typeLabel}</td>
                        {/* Confirmed */}
                        <td className="p-2 text-[11px] text-center border min-w-[65px]">{u.is_confirmed ? "Yes" : "No"}</td>

                        {/* Actions */}
                        <td className="p-2 text-xs border min-w-[210px]">
                          <div className="flex flex-col gap-1">
                            <div>
                              {showSet && (
                                <>
                                  <input
                                    type="number"
                                    placeholder="Set credits"
                                    className="border p-1 w-24"
                                    value={creditsToSet[u.id] ?? ""}
                                    onChange={e =>
                                      setCreditsToSet({ ...creditsToSet, [u.id]: e.target.value })
                                    }
                                    disabled={isSuper && !isSelf}
                                  />
                                  <button
                                    className="text-white px-2 py-1 rounded ml-1 hover:opacity-90"
                                    style={{ backgroundColor: "#00a7e1" }}
                                    onClick={() => handleSetCredits(u)}
                                    disabled={isSuper && !isSelf}
                                    title={
                                      isSuper && !isSelf
                                        ? "Cannot modify super admin's credits"
                                        : "Set credits"
                                    }
                                  >
                                    Set Credits
                                  </button>
                                </>
                              )}
                            </div>
                            <div>
                              {showEd && allowEdit && !isEditing && (
                                <button
                                  className="text-black px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#f5b800" }}
                                  onClick={() => handleEdit(u)}
                                  disabled={editingId !== null && editingId !== u.id}
                                >
                                  Edit User
                                </button>
                              )}
                              {showEd && allowEdit && isEditing && (
                                <button
                                  className="text-white px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#94c83d" }}
                                  onClick={() => handleSaveUser(u)}
                                >
                                  Save
                                </button>
                              )}
                            </div>                            
                            {/* Terminology button (only if the user has terminology) */}
                            {u.has_terminology && (
                              <div>
                                <button
                                  type="button"
                                  className="text-white px-2 py-1 rounded hover:opacity-90 text-xs"
                                  style={{ backgroundColor: "#006a93" }}
                                  onClick={() => handleUserTerminologyClick(u)}
                                  title="View terminology entries for this user"
                                >
                                  Terminology
                                </button>
                              </div>
                            )}

                            {/* TMX button (only if the user has TMX) */}
                            {u.has_tmx && (
                              <div>
                                <button
                                  type="button"
                                  className="text-white px-2 py-1 rounded hover:opacity-90 text-xs"
                                  style={{ backgroundColor: "#006a93" }}
                                  onClick={() => handleUserTMXClick(u)}
                                  title="View TMX entries for this user"
                                >
                                  TMX
                                </button>
                              </div>
                            )}
							
							<div className="flex flex-wrap items-center gap-x-1 gap-y-1">
                              {showMakeAdmin && (
                                <button
                                  className="text-white px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#94c83d" }}
                                  onClick={() => handleMakeAdmin(u)}
                                >
                                  Make Admin
                                </button>
                              )}
                              {showMakeAssistant && (
                                <button
                                  className="text-white px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#8f00e1" }}
                                  onClick={() => handleMakeAssistant(u)}
                                >
                                  Make Asst
                                </button>
                              )}
                              {showRemoveAssistant && (
                                <button
                                  className="text-white px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#e10053" }}
                                  onClick={() => handleRemoveAssistant(u)}
                                >
                                  Remove Asst
                                </button>
                              )}
                              {showRemoveAdmin && (
                                <button
                                  className="text-white px-2 py-1 rounded hover:opacity-90"
                                  style={{ backgroundColor: "#e10053" }}
                                  onClick={() => handleRemoveAdmin(u)}
                                >
                                  Remove Admin
                                </button>
                              )}
                            </div>
                            <div>
                              {showDel && (
                                <button
                                  className="bg-red-600 text-white px-2 py-1 rounded"
                                  onClick={() => handleDeleteUser(u)}
                                  title="Delete user"
                                >
                                  Delete User
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScrollContainer>
          </div>
          {message && (
            <div className="text-center mt-4" style={{ color: "#00a7e1" }}>
               {message}
             </div>
           )}
          </div>
        </>
      )}

      {/* PURCHASE HISTORY TAB */}
      
{/* PROJECT REQUESTS TAB */}
{tab === "requests" && (
  <div data-testid="admin-project-approvals-section">
    <h2 data-testid="admin-project-approvals-title" className="text-xl font-semibold mb-4">Project Approvals</h2>

    {message && (
      <div className="text-center mb-4" style={{ color: "#00a7e1" }}>
        {message}
      </div>
    )}

    {(editCostsModalOpen && editCostsRequest) ? (
      <div className="bg-white border rounded-lg p-4">
        <button
          className="text-sm font-semibold mb-4 hover:opacity-80"
          style={{ color: "#00a7e1" }}
          onClick={handleCloseEditCosts}
        >
          ← Back to Project Approvals
        </button>

        <h3 data-testid="admin-edit-costs-title" className="text-lg font-semibold mb-3">Edit Costs</h3>

        {editCostsLoadError ? (
          <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {editCostsLoadError}
          </div>
        ) : null}

        {!editCostsRequest ? (
          <div className="text-gray-600">Loading…</div>
        ) : (
          <div className="max-w-xl">
            <div className="text-sm text-gray-600 mb-3">
              {editCostsRequest.zip_filename ? <div><span className="font-semibold">Zip:</span> {editCostsRequest.zip_filename}</div> : null}
              <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 items-start">
                {Array.isArray(editCostsRequest.source_filenames) && editCostsRequest.source_filenames.length ? (
                  <div>
                    <span className="font-semibold">Source:</span> {editCostsRequest.source_filenames.join(", ")}
                  </div>
                ) : <div />}
                <div>
                  <span className="font-semibold">Additional Instructions:</span>{" "}
                  {String(editCostsRequest.additional_instructions || "").trim() ? (
                    <span className="whitespace-pre-wrap">{String(editCostsRequest.additional_instructions || "")}</span>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </div>
              </div>
              {Array.isArray(editCostsRequest.languages) && editCostsRequest.languages.length ? (
                <div className="mt-1">
                  <span className="font-semibold">Languages:</span> {editCostsRequest.languages.join(", ")}
                </div>
              ) : null}
              {String(editCostsRequest.status || "") === "Open Request" && getEstimatedRangeText(editCostsRequest.total || editCostsRequest.estimated_total) ? (
                <div className="mt-2">
                  <div className="font-semibold">Estimated Cost:</div>
                  <div>{getEstimatedRangeText(editCostsRequest.total || editCostsRequest.estimated_total)}</div>
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="text-sm">
                <div className="font-semibold mb-1">AI Translation</div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    className="w-full border rounded pl-7 pr-3 py-2"
                    value={editCostsAI}
                    onChange={(e) => setEditCostsAI(e.target.value)}
                    onBlur={(e) => setEditCostsAI(formatMoneyInput(e.target.value))}
                    placeholder="0.00"
                  />
                </div>
              </label>

              <label className="text-sm">
                <div className="font-semibold mb-1">Limited Review</div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    className="w-full border rounded pl-7 pr-3 py-2"
                    value={editCostsLR}
                    onChange={(e) => setEditCostsLR(e.target.value)}
                    onBlur={(e) => setEditCostsLR(formatMoneyInput(e.target.value))}
                    placeholder="0.00"
                  />
                </div>
              </label>

              <label className="text-sm">
                <div className="font-semibold mb-1">Post-Translation Formatting/QC</div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    className="w-full border rounded pl-7 pr-3 py-2"
                    value={editCostsFmt}
                    onChange={(e) => setEditCostsFmt(e.target.value)}
                    onBlur={(e) => setEditCostsFmt(formatMoneyInput(e.target.value))}
                    placeholder="0.00"
                  />
                </div>
              </label>

              <div className="text-sm">
                <div className="font-semibold mb-1">Total</div>
                <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
                  ${formatMoneyUSD(Number(String(editCostsAI || "0").replace(/,/g, "")) + Number(String(editCostsLR || "0").replace(/,/g, "")) + Number(String(editCostsFmt || "0").replace(/,/g, "")))}
                </div>
              </div>

              <label className="text-sm">
                <div className="font-semibold mb-1">Estimated Completion Date</div>
                <input
                  type="date"
                  className="w-[185px] border rounded px-3 py-2"
                  value={editCostsDate}
                  onChange={(e) => {
                    const v = e && e.target ? e.target.value : "";
                    setEditCostsDate(normalizeDateInputValue(v));
                  }}
                />
              </label>
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
              <button
                className="px-3 py-2 rounded text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "#00a7e1" }}
                onClick={handleSaveEditCosts}
                disabled={editCostsSaving || !editCostsRequest}
              >
                {editCostsSaving ? "Saving…" : "Save"}
              </button>

              <button
                className="px-3 py-2 rounded text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "#94c83d" }}
                onClick={handleSubmitCostsToUser}
                disabled={editCostsSubmitting || !editCostsRequest || !editCostsDate}
              >
                {editCostsSubmitting ? "Submitting…" : "Submit Costs to User"}
              </button>
            </div>

            {(editCostsRequest?.submitted_to_user_at || "").length > 0 && (
              <div className="mt-3 text-sm text-red-600">
                {(() => {
                  const ts = editCostsRequest?.submitted_to_user_at || "";
                  try {
                    const dt = new Date(normalizeTimestampForLocalDisplay(ts));
                    if (isNaN(dt.getTime())) return `${ts} - Costs submitted to user.`;
                    return `${dt.toLocaleString()} - Costs submitted to user.`;
                  } catch (e) {
                    return `${ts} - Costs submitted to user.`;
                  }
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    ) : null}

    {!(editCostsModalOpen && editCostsRequest) && (projectRequestsLoading ? (
      <div data-testid="admin-project-approvals-loading" className="text-gray-600">Loading…</div>
    ) : projectRequests.length === 0 ? (
      <div data-testid="admin-project-approvals-empty" className="text-gray-600">No project requests found.</div>
    ) : (
      <TableScrollContainer>
        <table data-testid="admin-project-approvals-table" className="min-w-full border text-sm">
          <thead>
            <tr>
              <th
                className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[140px] cursor-pointer select-none"
                onClick={() => toggleApprovalsSort("estimate")}
                title="Sort by Estimate"
              >
                Estimate{approvalsSortIndicator("estimate")}
              </th>
              <th
                className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[220px] cursor-pointer select-none"
                onClick={() => toggleApprovalsSort("user")}
                title="Sort by User"
                data-testid="admin-project-approvals-sort-user"
              >
                User{approvalsSortIndicator("user")}
              </th>
              <th
                className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[140px] cursor-pointer select-none"
                onClick={() => toggleApprovalsSort("date")}
                title="Sort by Date"
              >
                Date{approvalsSortIndicator("date")}
              </th>
              <th
                className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[120px] cursor-pointer select-none"
                onClick={() => toggleApprovalsSort("status")}
                title="Sort by Status"
              >
                Status{approvalsSortIndicator("status")}
              </th>
              <th className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[220px]">Source Filenames</th>
              <th className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[180px]">Languages</th>
              <th className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[280px]">Cost Breakdown</th>
              <th className="sticky top-0 bg-white z-10 p-2 text-left border min-w-[120px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedProjectRequests.map((r, idx) => {
              const ts = String(r.created_at || "");
              let datePart = "-";
              let timePart = "";
              if (ts) {
                const d = new Date(normalizeTimestampForLocalDisplay(ts));
                if (!Number.isNaN(d.getTime())) {
                  // Match the viewer's locale + time zone (browser settings)
                  datePart = d.toLocaleDateString();
                  timePart = d.toLocaleTimeString();
                } else {
                  // Fallback for unexpected formats
                  datePart = ts.indexOf("T") !== -1 ? ts.split("T")[0] : ts.split(" ")[0];
                  timePart = ts.indexOf("T") !== -1 ? (ts.split("T")[1] || "") : (ts.split(" ")[1] || "");
                  timePart = String(timePart).replace("Z", "").split(".")[0];
                }
              }


const estimateCode = getApprovalsEstimateCode(r);
              return (
<tr
  key={r.task_id || idx}
  style={getProjectRequestRowStyle(r.status, r.user_decision)}
  data-testid="admin-project-approval-row"
>
                
                <td className="p-2 border align-top min-w-[140px]">
                  <div className="text-xs leading-snug break-words whitespace-normal">{estimateCode || "-"}</div>
                </td>
<td className="p-2 border align-top">
                  <div className="text-xs leading-snug">
                    <div className="font-semibold">
                      {([
                        String(r?.user_first_name || "").trim(),
                        String(r?.user_last_name || "").trim(),
                      ].filter(Boolean).join(" ") || "(Unknown)")}
                    </div>
                    <div className="text-gray-700 break-all">
                      {String(r?.user_email || "").trim() || ""}
                    </div>
                  </div>
                </td>
                <td className="p-2 border align-top">
                  {ts ? (
                    <div className="leading-tight">
                      <div>{datePart}</div>
                      <div className="text-gray-600 text-xs">{timePart || ""}</div>
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className="p-2 border align-top">
                  {(() => {
                    const s = String(r.status || "");
                    if (s === "Sent to Ops") return "Quote Approved";
                    return s || "Open Request";
                  })()}
                </td>
                <td className="p-2 border align-top">
                  <div className="text-xs break-words whitespace-normal">{Array.isArray(r.source_filenames) && r.source_filenames.length ? (
                    <ul className="list-disc pl-5 text-xs break-words whitespace-normal">
                      {r.source_filenames.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </div>
                </td>
                <td className="p-2 border align-top">
                  <div className="text-[11px] break-words whitespace-normal">{Array.isArray(r.languages) && r.languages.length ? (
                    <ul className="list-disc pl-5 text-xs break-words whitespace-normal">
                      {r.languages.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </div>
                </td>
                <td className="p-2 border align-top">
                  <div className="text-xs break-words whitespace-normal">
                    {(() => {
                      const lines = buildCostBreakdownForRequestsTable(r);
                      if (!lines.length) return <span className="text-gray-500">—</span>;
                      return (
                        <div className="space-y-0.5">
                          {lines.map((c, i) => {
                            const isTotal = /^\s*total\s*:/i.test(String(c || ""));
                            return (
                              <div key={i} className={isTotal ? "pt-1 font-semibold" : ""}>
                                {c}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className="p-2 border align-top min-w-[120px] w-[120px]">
                  <div className="flex flex-col gap-2">
                    {/* Prefer bundled zip if available; otherwise allow separate source/translated downloads */}
                    {/*
                      Download bundled source + translated ZIP (same bundle used in the quote email).
                    */}
                    <button
                      className="px-2 py-1 rounded text-white hover:opacity-90 text-xs whitespace-nowrap w-full"
                      style={{ backgroundColor: "#00a7e1" }}
                      onClick={() => handleDownloadProjectRequest(r)}
                      title="Download source + translated bundle"
                    >
                      Download
                    </button>

                    <button
                      className="px-2 py-1 rounded text-black hover:opacity-90 text-xs whitespace-nowrap w-full"
                      style={{ backgroundColor: "#f5b800" }}
                      onClick={() => handleOpenEditCosts(r)}
                      title="Edit costs"
                    >
                      Edit
                    </button>

                    <button
                      className="px-2 py-1 rounded text-white hover:opacity-90 text-xs whitespace-nowrap w-full"
                      style={{ backgroundColor: "#dc2626" }}
                      onClick={() => handleDeleteProjectRequest(r)}
                      title="Delete project request"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
          </TableScrollContainer>
    ))}
  </div>
)}

      {!isAssistantOnly && tab === "history" && (
        <PurchaseHistory />
      )}

      {(isUserAdmin || isUserSuper) && tab === "payments" && (
        <PaymentTracking />
      )}

      {(isUserAdmin || isUserSuper) && tab === "abuse" && (
        <div data-testid="admin-abuse-protection-section">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label htmlFor="admin-abuse-search" className="text-xs font-semibold mb-1">
                Search
              </label>
              <input
                id="admin-abuse-search"
                type="text"
                className="border p-2 rounded text-sm min-w-[300px]"
                placeholder="Search e-mail, IP, event, endpoint, or reason…"
                value={abuseSearch}
                onChange={(e) => setAbuseSearch(e.target.value)}
              />
            </div>
            {abuseSearch.trim() !== "" && (
              <button
                type="button"
                className="underline text-xs hover:opacity-90 mb-2"
                style={{ color: "#00a7e1" }}
                onClick={() => setAbuseSearch("")}
              >
                Clear search
              </button>
            )}
            <button
              type="button"
              className="text-white px-3 py-2 rounded text-sm hover:opacity-90"
              style={{ backgroundColor: "#00a7e1" }}
              onClick={() => setRefreshFlag((f) => f + 1)}
            >
              Refresh
            </button>
          </div>

          {message && (
            <div className="mb-3 text-sm font-semibold" style={{ color: "#00a7e1" }}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
            {[
              ["Blocked Emails", abuseData.stats?.active_suppressed_emails || 0],
              ["Blocked IPs", abuseData.stats?.active_blocked_ips || 0],
              ["Threat Indicators", abuseData.stats?.active_threat_indicators || 0],
              ["Honeypot Hits", abuseData.stats?.honeypot_hits || 0],
              ["CAPTCHA Failures", abuseData.stats?.captcha_failures || 0],
              ["Registration Abuse", abuseData.stats?.registration_abuse || 0],
              ["Webhook Events", abuseData.stats?.webhook_events || 0],
              ["Rate-Limit Events", abuseData.stats?.rate_limit_events || 0],
            ].map(([label, value]) => (
              <div key={label} className="border rounded-lg p-3 bg-gray-50">
                <div className="text-xs text-gray-600">{label}</div>
                <div className="text-2xl font-semibold mt-1">{Number(value).toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div className="mb-4 flex gap-2 flex-wrap">
            {[
              ["suppressed", "Blocked Emails"],
              ["blocked", "Blocked IPs"],
              ["threat", "Threat Intelligence"],
              ["honeypot", "Honeypot Hits"],
              ["captcha", "CAPTCHA Failures"],
              ["registration", "Registration Abuse"],
              ["webhooks", "Webhook History"],
              ["limits", "Rate-Limit Events"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={key === "suppressed" ? "admin-abuse-section-blocked-emails" : `admin-abuse-section-${key}`}
                className={`px-3 py-1 rounded ${abuseSection === key ? "text-white" : "bg-gray-200"}`}
                style={{ backgroundColor: abuseSection === key ? "#006a93" : undefined }}
                onClick={() => setAbuseSection(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {abuseSection === "suppressed" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-1">Add Blocked Email or Domain</h3>
                <div className="text-xs text-gray-600 mb-3">Enter a full e-mail address or a bare domain such as example.com. A domain block also covers its subdomains.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-sm">
                    <div className="font-semibold mb-1">E-mail Address or Domain</div>
                    <input
                      type="text"
                      className="border rounded p-2 w-full bg-white"
                      placeholder="user@example.com or example.com"
                      value={newBlockedEmail}
                      onChange={(e) => setNewBlockedEmail(e.target.value)}
                    />
                  </label>
                  <label className="text-sm">
                    <div className="font-semibold mb-1">Reason for Block</div>
                    <input
                      type="text"
                      className="border rounded p-2 w-full bg-white"
                      placeholder="Reason for blocking this address or domain"
                      value={newBlockedEmailReason}
                      onChange={(e) => setNewBlockedEmailReason(e.target.value)}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="mt-3 text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: "#e10053" }}
                  onClick={handleAddBlockedEmail}
                  disabled={abuseActionLoading || !newBlockedEmail.trim() || !newBlockedEmailReason.trim()}
                >
                  Add Block
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-1">Import Blocked Emails</h3>
                <div className="text-xs text-gray-600 mb-3">
                  TXT files should be tab-delimited. CSV and XLSX are also supported. The first column may contain a full e-mail address or a bare domain such as example.com; the optional second column is Reason for Block. Headers are optional.
                </div>
                <input
                  ref={blockedEmailImportRef}
                  type="file"
                  accept=".txt,.csv,.xlsx"
                  className="block w-full text-sm mb-3"
                  onChange={(e) => setBlockedEmailImportFile(e.target.files?.[0] || null)}
                />
                <label className="text-sm block">
                  <div className="font-semibold mb-1">Default Reason for Block</div>
                  <input
                    type="text"
                    className="border rounded p-2 w-full bg-white"
                    value={blockedEmailImportReason}
                    onChange={(e) => setBlockedEmailImportReason(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="mt-3 text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: "#006a93" }}
                  onClick={handleImportBlockedEmails}
                  disabled={abuseActionLoading || !blockedEmailImportFile}
                >
                  Import List
                </button>
              </div>
            </div>
          )}

          {abuseSection === "blocked" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-3">Add Blocked IP</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-sm">
                    <div className="font-semibold mb-1">IP Address</div>
                    <input
                      type="text"
                      className="border rounded p-2 w-full bg-white"
                      placeholder="192.0.2.1 or IPv6 address"
                      value={newBlockedIP}
                      onChange={(e) => setNewBlockedIP(e.target.value)}
                    />
                  </label>
                  <label className="text-sm">
                    <div className="font-semibold mb-1">Reason for Block</div>
                    <input
                      type="text"
                      className="border rounded p-2 w-full bg-white"
                      placeholder="Reason for blocking this IP"
                      value={newBlockedIPReason}
                      onChange={(e) => setNewBlockedIPReason(e.target.value)}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="mt-3 text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: "#e10053" }}
                  onClick={handleAddBlockedIP}
                  disabled={abuseActionLoading || !newBlockedIP.trim() || !newBlockedIPReason.trim()}
                >
                  Add Block
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-1">Import Blocked IPs</h3>
                <div className="text-xs text-gray-600 mb-3">
                  TXT files should be tab-delimited. CSV and XLSX are also supported. Use the first column for the IP address and an optional second column for Reason for Block. Headers are optional.
                </div>
                <input
                  ref={blockedIPImportRef}
                  type="file"
                  accept=".txt,.csv,.xlsx"
                  className="block w-full text-sm mb-3"
                  onChange={(e) => setBlockedIPImportFile(e.target.files?.[0] || null)}
                />
                <label className="text-sm block">
                  <div className="font-semibold mb-1">Default Reason for Block</div>
                  <input
                    type="text"
                    className="border rounded p-2 w-full bg-white"
                    value={blockedIPImportReason}
                    onChange={(e) => setBlockedIPImportReason(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="mt-3 text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: "#006a93" }}
                  onClick={handleImportBlockedIPs}
                  disabled={abuseActionLoading || !blockedIPImportFile}
                >
                  Import List
                </button>
              </div>
            </div>
          )}

          {abuseLoading ? (
            <div data-testid="admin-abuse-protection-loading" className="text-gray-600">Loading...</div>
          ) : abuseSection === "suppressed" ? (
            <div>
              <h3 className="text-lg font-semibold mb-3">Blocked Emails/Domains</h3>
              <div className="mb-3 flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                  <label htmlFor="admin-blocked-email-search" className="text-xs font-semibold mb-1">Search</label>
                  <input
                    id="admin-blocked-email-search"
                    type="text"
                    className="border p-2 rounded text-sm min-w-[300px]"
                    placeholder="Search e-mail, domain, reason, source, or event…"
                    value={blockedEmailSearch}
                    onChange={(e) => setBlockedEmailSearch(e.target.value)}
                  />
                </div>
                {blockedEmailSearch.trim() !== "" && (
                  <button
                    type="button"
                    className="underline text-xs hover:opacity-90 mb-2"
                    style={{ color: "#00a7e1" }}
                    onClick={() => setBlockedEmailSearch("")}
                  >
                    Clear search
                  </button>
                )}
              </div>
              <TableScrollContainer>
              <table data-testid="admin-abuse-protection-table" className="min-w-full border text-xs">
                <thead><tr>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">E-mail / Domain</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Type</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Source / Event</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Reason</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">First Seen</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Last Seen</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Count</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Active</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Action</th>
                </tr></thead>
                <tbody>
                  {filteredSuppressedEmails.length === 0 && <tr><td data-testid="admin-abuse-empty" colSpan={9} className="text-center text-gray-500 py-6">No blocked e-mail addresses or domains found.</td></tr>}
                  {visibleSuppressedEmails.map((row, idx) => (
                    <tr key={row.id || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                      <td className="p-2 border break-all">{row.block_value || row.email || row.domain}</td>
                      <td className="p-2 border">{row.block_type || "E-mail"}</td>
                      <td className="p-2 border">{row.source ? `${row.source}${row.mailtrap_event ? `: ${row.mailtrap_event}` : ""}` : (row.mailtrap_event || "")}</td>
                      <td className="p-2 border">{row.reason || ""}</td>
                      <td className="p-2 border whitespace-nowrap">{row.first_seen ? new Date(normalizeTimestampForLocalDisplay(row.first_seen)).toLocaleString() : ""}</td>
                      <td className="p-2 border whitespace-nowrap">{row.last_seen ? new Date(normalizeTimestampForLocalDisplay(row.last_seen)).toLocaleString() : ""}</td>
                      <td className="p-2 border text-center">{row.times_seen || 0}</td>
                      <td className="p-2 border text-center">{row.active ? "Yes" : "No"}</td>
                      <td className="p-2 border text-center">
                        {row.active ? <button className="text-white px-2 py-1 rounded" style={{ backgroundColor: "#e10053" }} onClick={() => handleRemoveEmailBlock(row)}>Remove Block</button> : <span className="text-gray-500">Removed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </TableScrollContainer>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="text-gray-600">
                  Showing {visibleSuppressedEmails.length.toLocaleString()} of {filteredSuppressedEmails.length.toLocaleString()} blocked e-mail/domain entr{filteredSuppressedEmails.length === 1 ? "y" : "ies"}
                </div>
                <label className="flex items-center gap-2">
                  <span className="font-semibold">Display:</span>
                  <select
                    className="border rounded p-1 bg-white"
                    value={blockedEmailDisplaySize}
                    onChange={(e) => setBlockedEmailDisplaySize(e.target.value === "all" ? "all" : Number(e.target.value))}
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                    <option value="all">All</option>
                  </select>
                </label>
              </div>
            </div>
          ) : abuseSection === "blocked" ? (
            <TableScrollContainer>
              <table className="min-w-full border text-xs">
                <thead><tr>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">IP Address</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Reason</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Blocked Until</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Last Seen</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Count</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Active</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Action</th>
                </tr></thead>
                <tbody>
                  {filteredBlockedIPs.length === 0 && <tr><td data-testid="admin-abuse-empty-blocked" colSpan={7} className="text-center text-gray-500 py-6">No blocked IP addresses found.</td></tr>}
                  {filteredBlockedIPs.map((row, idx) => (
                    <tr key={row.id || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                      <td className="p-2 border">{row.ip_address}</td>
                      <td className="p-2 border">{row.reason}</td>
                      <td className="p-2 border whitespace-nowrap">{row.blocked_until ? new Date(normalizeTimestampForLocalDisplay(row.blocked_until)).toLocaleString() : "Manual"}</td>
                      <td className="p-2 border whitespace-nowrap">{row.last_seen ? new Date(normalizeTimestampForLocalDisplay(row.last_seen)).toLocaleString() : ""}</td>
                      <td className="p-2 border text-center">{row.times_seen || 0}</td>
                      <td className="p-2 border text-center">{row.active ? "Yes" : "No"}</td>
                      <td className="p-2 border text-center">
                        {row.active ? <button className="text-white px-2 py-1 rounded" style={{ backgroundColor: "#e10053" }} onClick={() => handleRemoveIPBlock(row)}>Remove Block</button> : <span className="text-gray-500">Removed/Expired</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScrollContainer>
          ) : abuseSection === "threat" ? (
            <div className="space-y-5">
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-semibold">External Threat Intelligence</h3>
                    <div className="text-xs text-gray-600 mt-1">Spamhaus DROP and the disposable-domain feed require no API key. AbuseIPDB is used when ABUSEIPDB_API_KEY is configured on the backend. The existing hourly cron checks these feeds and refreshes stale data approximately once per day.</div>
                  </div>
                  <button type="button" className="text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: "#00a7e1" }} onClick={handleRefreshThreatIntel} disabled={abuseActionLoading}>
                    {abuseActionLoading ? "Working..." : "Refresh Feeds Now"}
                  </button>
                </div>
                <TableScrollContainer maxHeightClass="max-h-[35vh]">
                  <table className="min-w-full border text-xs bg-white">
                    <thead><tr>
                      <th className="sticky top-0 bg-white z-10 p-2 border text-left">Source</th>
                      <th className="sticky top-0 bg-white z-10 p-2 border">Indicators</th>
                      <th className="sticky top-0 bg-white z-10 p-2 border">Last Success</th>
                      <th className="sticky top-0 bg-white z-10 p-2 border text-left">Status / Error</th>
                    </tr></thead>
                    <tbody>
                      {filteredThreatFeeds.length === 0 && <tr><td colSpan={4} className="text-center text-gray-500 py-5">No threat feeds have been refreshed yet.</td></tr>}
                      {filteredThreatFeeds.map((row, idx) => (
                        <tr key={row.id || row.source || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                          <td className="p-2 border font-semibold">{row.source}</td>
                          <td className="p-2 border text-center">{Number(row.item_count || 0).toLocaleString()}</td>
                          <td className="p-2 border whitespace-nowrap">{row.last_success_at ? new Date(normalizeTimestampForLocalDisplay(row.last_success_at)).toLocaleString() : "Never"}</td>
                          <td className="p-2 border">{row.last_error ? <span className="text-red-600">{row.last_error}</span> : (row.enabled === false ? "Disabled" : "OK")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScrollContainer>
                <div className="text-[11px] text-gray-500 mt-2">Spamhaus DROP data used with credit to The Spamhaus Project.</div>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-1">Threat-Intelligence Allowlist</h3>
                <div className="text-xs text-gray-600 mb-3">Use this only to exempt a trusted IP, CIDR range, or domain from external threat-feed blocking. It does not override a manual Admin block.</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <label className="text-sm">
                    <div className="font-semibold mb-1">Type</div>
                    <select className="border rounded p-2 w-full bg-white" value={threatAllowType} onChange={(e) => setThreatAllowType(e.target.value)}>
                      <option value="ip">IP Address</option>
                      <option value="cidr">IP Range (CIDR)</option>
                      <option value="domain">E-mail Domain</option>
                    </select>
                  </label>
                  <label className="text-sm md:col-span-1">
                    <div className="font-semibold mb-1">Value</div>
                    <input type="text" className="border rounded p-2 w-full bg-white" placeholder={threatAllowType === "domain" ? "example.com" : (threatAllowType === "cidr" ? "192.0.2.0/24" : "192.0.2.1")} value={threatAllowValue} onChange={(e) => setThreatAllowValue(e.target.value)} />
                  </label>
                  <label className="text-sm md:col-span-1">
                    <div className="font-semibold mb-1">Reason</div>
                    <input type="text" className="border rounded p-2 w-full bg-white" placeholder="Trusted customer/network" value={threatAllowReason} onChange={(e) => setThreatAllowReason(e.target.value)} />
                  </label>
                  <button type="button" className="text-white px-3 py-2 rounded text-sm hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: "#94c83d" }} onClick={handleAddThreatAllowlist} disabled={abuseActionLoading || !threatAllowValue.trim()}>Add Allowlist Entry</button>
                </div>
                <div className="mt-4">
                  <TableScrollContainer maxHeightClass="max-h-[30vh]">
                    <table className="min-w-full border text-xs bg-white">
                      <thead><tr>
                        <th className="sticky top-0 bg-white z-10 p-2 border text-left">Type</th>
                        <th className="sticky top-0 bg-white z-10 p-2 border text-left">Value</th>
                        <th className="sticky top-0 bg-white z-10 p-2 border text-left">Reason</th>
                        <th className="sticky top-0 bg-white z-10 p-2 border">Action</th>
                      </tr></thead>
                      <tbody>
                        {filteredThreatAllowlist.length === 0 && <tr><td colSpan={4} className="text-center text-gray-500 py-5">No threat-intelligence allowlist entries.</td></tr>}
                        {filteredThreatAllowlist.map((row, idx) => (
                          <tr key={row.id || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                            <td className="p-2 border">{row.indicator_type}</td>
                            <td className="p-2 border break-all">{row.indicator}</td>
                            <td className="p-2 border">{row.reason || ""}</td>
                            <td className="p-2 border text-center"><button className="text-white px-2 py-1 rounded" style={{ backgroundColor: "#e10053" }} onClick={() => handleRemoveThreatAllowlist(row)}>Remove</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScrollContainer>
                </div>
              </div>
            </div>
          ) : abuseSection === "webhooks" ? (
            <TableScrollContainer>
              <table className="min-w-full border text-xs">
                <thead><tr>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Received</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Event</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">E-mail</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Event ID</th>
                </tr></thead>
                <tbody>
                  {filteredWebhookHistory.length === 0 && <tr><td data-testid="admin-abuse-empty-webhooks" colSpan={4} className="text-center text-gray-500 py-6">No webhook events found.</td></tr>}
                  {filteredWebhookHistory.map((row, idx) => (
                    <tr key={row.id || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                      <td className="p-2 border whitespace-nowrap">{row.received_at ? new Date(normalizeTimestampForLocalDisplay(row.received_at)).toLocaleString() : ""}</td>
                      <td className="p-2 border">{row.event_type}</td>
                      <td className="p-2 border break-all">{row.email || ""}</td>
                      <td className="p-2 border break-all">{row.event_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScrollContainer>
          ) : (
            <TableScrollContainer>
              <table className="min-w-full border text-xs">
                <thead><tr>
                  <th className="sticky top-0 bg-white z-10 p-2 border">Time</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Event</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">IP Address</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">E-mail</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Endpoint</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border text-left">Details</th>
                </tr></thead>
                <tbody>
                  {filteredAbuseEvents.filter((row) => {
                    if (abuseSection === "honeypot") return ["honeypot_hit", "registration_honeypot"].includes(row.event_type);
                    if (abuseSection === "captcha") return row.event_type === "registration_captcha_failure";
                    if (abuseSection === "registration") return String(row.event_type || "").startsWith("registration_") && row.event_type !== "registration_success";
                    if (abuseSection === "limits") return ["rate_limit", "registration_rate_limit", "blocked_ip_attempt", "ip_blocked"].includes(row.event_type);
                    return true;
                  }).length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-6">No matching abuse events found.</td></tr>}
                  {filteredAbuseEvents.filter((row) => {
                    if (abuseSection === "honeypot") return ["honeypot_hit", "registration_honeypot"].includes(row.event_type);
                    if (abuseSection === "captcha") return row.event_type === "registration_captcha_failure";
                    if (abuseSection === "registration") return String(row.event_type || "").startsWith("registration_") && row.event_type !== "registration_success";
                    if (abuseSection === "limits") return ["rate_limit", "registration_rate_limit", "blocked_ip_attempt", "ip_blocked"].includes(row.event_type);
                    return true;
                  }).map((row, idx) => (
                    <tr key={row.id || idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                      <td className="p-2 border whitespace-nowrap">{row.created_at ? new Date(normalizeTimestampForLocalDisplay(row.created_at)).toLocaleString() : ""}</td>
                      <td className="p-2 border">{row.event_type}</td>
                      <td className="p-2 border">{row.ip_address || ""}</td>
                      <td className="p-2 border break-all">{row.email || ""}</td>
                      <td className="p-2 border">{row.endpoint || ""}</td>
                      <td className="p-2 border">{row.detail || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScrollContainer>
          )}
        </div>
      )}

      {(isUserAdmin || isUserSuper) && tab === "audit" && (
        <div data-testid="admin-history-section">
          <div className="mb-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold mb-1">Search</label>
              <input
                type="text"
                className="border p-2 rounded text-sm"
                placeholder="Action, actor, target…"
                data-testid="admin-history-search"
                value={auditSearch}
                onChange={e => setAuditSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Action</label>
              <input
                type="text"
                className="border p-2 rounded text-sm"
                placeholder="set_credits"
                value={auditAction}
                onChange={e => setAuditAction(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Target Email</label>
              <input
                type="text"
                className="border p-2 rounded text-sm"
                placeholder="user@example.com"
                value={auditTargetEmail}
                onChange={e => setAuditTargetEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Days</label>
              <input
                type="number"
                min="0"
                className="border p-2 rounded text-sm w-24"
                value={auditDays}
                onChange={e => setAuditDays(e.target.value)}
              />
            </div>
            <button
              className="text-white px-3 py-2 rounded text-sm hover:opacity-90"
              style={{ backgroundColor: "#00a7e1" }}
              onClick={() => setRefreshFlag(f => f + 1)}
            >
              Refresh
            </button>
            <button
              className="text-white px-3 py-2 rounded text-sm hover:opacity-90"
              style={{ backgroundColor: "#94c83d" }}
              onClick={handleAuditExport}
            >
              Export CSV
            </button>
          </div>

          <div data-testid="admin-history-status" className="mb-2 text-sm text-gray-600">
            {auditLoading ? "Loading..." : `Showing ${auditRows.length} audit entries`}
          </div>

          <TableScrollContainer>
            <table data-testid="admin-history-table" className="min-w-full border text-xs">
              <thead>
                <tr>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[150px]">Time</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[180px]">Admin</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[160px]">Action</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[140px]">Target Type</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[140px]">Target ID</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[200px]">Target Email</th>
                  <th className="sticky top-0 bg-white z-10 p-2 border min-w-[100px]">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.length === 0 && (
                  <tr>
                    <td data-testid="admin-history-empty" colSpan={7} className="text-center text-gray-500 py-6">
                      No audit entries found.
                    </td>
                  </tr>
                )}
                {auditRows.map((row, idx) => (
                  <tr key={row.id || idx} className={(idx % 2 === 0) ? "bg-white" : "bg-gray-50"}>
                    <td className="p-2 border whitespace-nowrap">
                      {row.created_at ? String(row.created_at).replace("T", " ").replace("Z", "") : ""}
                    </td>
                    <td className="p-2 border break-all">{row.actor_email || ""}</td>
                    <td className="p-2 border font-semibold">{row.action || ""}</td>
                    <td className="p-2 border">{row.target_type || ""}</td>
                    <td className="p-2 border break-all">{row.target_id || ""}</td>
                    <td className="p-2 border break-all">{row.target_email || ""}</td>
                    <td className="p-2 border text-center">
                      <button
                        className="bg-white border border-gray-300 text-xs px-2 py-1 rounded hover:bg-gray-50"
                        onClick={() => handleAuditDetailsDownload(row)}
                        title="Download details.txt for this entry"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>
        </div>
      )}
      
	  {/* DRAFTS TAB */}
      {!isAssistantOnly && tab === "drafts" && (
        <div data-testid="admin-drafts-section">
          <div className="mb-4 flex flex-col items-start">
            <div className="flex items-center w-full max-w-md">
              <label className="font-semibold mr-2 whitespace-nowrap">Search:</label>
              <input
                type="text"
                placeholder="Search by email or company…"
                data-testid="admin-drafts-search"
                className="border p-2 rounded flex-1"
                value={draftSearch}
                onChange={e => setDraftSearch(e.target.value)}
              />
            </div>
            {draftSearch.trim() !== "" && (
              <button
                className="mt-2 underline text-xs hover:opacity-90"
                style={{ color: "#00a7e1" }}
                onClick={() => setDraftSearch("")}
                data-testid="admin-drafts-search-clear"
              >
                Clear search
              </button>
            )}
          </div>

          <div data-testid="admin-drafts-status" className="mb-2 text-sm text-gray-600">
            {draftLoading ? "Loading..." : `Showing ${drafts.length} drafts`}
          </div>

          <TableScrollContainer>
            <table data-testid="admin-drafts-table" className="min-w-full border">
              <thead>
                <tr>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[120px] cursor-pointer select-none" onClick={() => toggleDraftsSort("updated")}>Updated{draftsSortIndicator("updated")}</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[160px] cursor-pointer select-none" onClick={() => toggleDraftsSort("email")}>Email{draftsSortIndicator("email")}</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[160px] cursor-pointer select-none" onClick={() => toggleDraftsSort("name")}>Name{draftsSortIndicator("name")}</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[120px] cursor-pointer select-none" onClick={() => toggleDraftsSort("company")}>Company{draftsSortIndicator("company")}</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[80px] text-center">Step</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[200px]">Pairs</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[180px] text-center">Types</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[180px]">Details</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[110px] text-center">Status</th>
                  <th className="sticky top-0 bg-white z-10 p-2 text-xs border min-w-[240px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drafts.length === 0 && (
                  <tr>
                    {/* colSpan updated from 10 to 11 to match added column */}
                    <td colSpan={10} className="text-center text-gray-500 py-6">
                      No draft users found.
                    </td>
                  </tr>
                )}
                {sortedDraftUsers.map((d, idx) => {
                  const fileTypes = Array.isArray(d.file_types) ? d.file_types : [];

                  return (
                    <tr key={d.id} className={(idx % 2 === 0) ? "bg-white" : "bg-gray-50"}>
                      <td className="p-2 text-xs border">
                        {d.updated_at?.replace("T", " ").replace("Z","") || ""}
                      </td>
                      <td className="p-2 text-xs border break-all">
                        {d.email || <span className="text-gray-400">(none)</span>}
                      </td>
                      <td className="p-2 text-xs border">
                        {[d.first_name, d.last_name].filter(Boolean).join(" ")}
                      </td>
                      <td className="p-2 text-xs border">{d.company || ""}</td>
                      <td className="p-2 text-xs border text-center">{d.step ?? 0}</td>
                      <td className="p-2 text-[10px] border align-top">
                        {d.pairs_summary ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {d.pairs_summary.split(",").map((p, i) => (
                              <li key={i}>{p.trim()}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      {/* Types column: list file types as bullet points */}
                      <td className="p-2 text-[10px] border align-top min-w-[180px]">
                        {fileTypes.length ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {fileTypes.map((ft) => (
                              <li key={ft}>{ft}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      {/* Details column: bullet list instead of colored bubbles */}
                      <td className="p-2 text-xs border align-top">
                        <ul className="list-disc list-inside text-[10px] text-gray-700 space-y-0.5">
                          {d.other_file_type && (
                            <li>Custom file type: {d.other_file_type}</li>
                          )}
                          {d.other_sample_filename && (
                            <li>Sample file: {d.other_sample_filename}</li>
                          )}
                          {d.has_terminology && <li>Terminology</li>}
                          {d.has_tmx && <li>TMX</li>}
                          {d.data_usage_mode && d.data_usage_mode !== "no_share" && (
                            <li>Usage: {d.data_usage_mode}</li>
                          )}
                          {!d.other_file_type &&
                            !d.other_sample_filename &&
                            !d.has_terminology &&
                            !d.has_tmx &&
                            !(d.data_usage_mode && d.data_usage_mode !== "no_share") && (
                              <li>No additional details</li>
                            )}
                        </ul>
                      </td>

                      <td className="p-2 text-xs border">
                        {d.contact_stage || "New"}
                      </td>

                      <td className="p-2 text-xs border">
                        <div className="flex flex-wrap gap-1">
                          {/* Convert – keep existing green style */}
                          <button
                            className="text-white px-2 py-1 rounded hover:opacity-90 text-xs"
                            style={{ backgroundColor: "#94c83d" }}
                            onClick={() => handleDraftConvert(d)}
                            disabled={!d.email || d.linked_user_id}
                            title="Convert to User"
                          >
                            Convert
                          </button>

                          {/* Terminology – open modal, same dark blue as Users tab */}
                          {d.has_terminology && (
                            <button
                              className="text-white px-2 py-1 rounded hover:opacity-90 text-xs"
                              style={{ backgroundColor: "#006a93" }}
                              onClick={() => handleDraftTerminologyClick(d)}
                              title="View terminology entries for this draft"
                            >
                              Terminology
                            </button>
                          )}

                          {/* TMX – open modal for draft TMX entries */}
                          {d.has_tmx && (
                            <button
                              className="text-white px-2 py-1 rounded hover:opacity-90 text-xs"
                              style={{ backgroundColor: "#006a93" }}
                              onClick={() => handleDraftTMXClick(d)}
                              title="View TMX entries for this draft"
                            >
                              TMX
                            </button>
                          )}

                          {/* Delete – unchanged */}
                          <button
                            className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                            onClick={() => handleDraftDelete(d)}
                            title="Delete Draft"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
      </TableScrollContainer>
          {message && (
            <div
              className="mt-3 text-sm font-semibold"
              style={{ color: "#00a7e1" }}
              aria-live="polite"
            >
              {message}
            </div>
          )}
        </div>
      )}

      {/* Terminology modal for regular Users */}
      {termModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4">
            <h3 className="text-lg font-semibold mb-2">
              Terminology for {termModalUser.email}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              View terminology entries associated with this user. Entries without a file were imported as prompts only.
            </div>

            <div className="max-h-64 overflow-y-auto divide-y border rounded">
              {termModalItems.map((item) => {
                const src = item.source_code || "";
                const tgt = item.target_code || "";
                const pair =
                  src && tgt ? `${src} → ${tgt}` : "Unspecified pair";
                const hasFile = !!item.s3_key;

                return (
                  <div key={item.id} className="p-2 flex items-center justify-between gap-2">
                    <div className="text-xs leading-snug">
                      <div className="font-semibold">{pair}</div>
                      <div className="text-gray-600">
                        {item.filename || "terminology.csv"}
                      </div>
                      {!hasFile && (
                        <div className="text-[11px] text-red-500">
                          Prompt-only entry (no file stored)
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded text-xs ${
                          hasFile
                            ? "text-white hover:opacity-90"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                        style={hasFile ? { backgroundColor: "#00a7e1" } : {}}
                        onClick={() =>
                          hasFile &&
                          downloadUserTerminologyFile(termModalUser.id, item)
                        }
                        disabled={!hasFile}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded border text-sm hover:bg-gray-100"
                onClick={() => {
                  setTermModalUser(null);
                  setTermModalItems([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TMX modal for regular Users */}
      {tmxModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4">
            <h3 className="text-lg font-semibold mb-2">
              TMX for {tmxModalUser.email}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              View TMX entries associated with this user. Entries without a file are not downloadable.
            </div>

            <div className="max-h-64 overflow-y-auto divide-y border rounded">
              {tmxModalItems.map((item) => {
                const src = item.source_code || "";
                const tgt = item.target_code || "";
                const pair =
                  src && tgt ? `${src} → ${tgt}` : "Unspecified pair";
                const hasFile = !!item.s3_key;
                const filename = item.filename || "tmx.tmx";

                return (
                  <div
                    key={item.id}
                    className="p-2 flex items-center justify-between gap-2"
                  >
                    <div className="text-xs leading-snug">
                      <div className="font-semibold">{pair}</div>
                      <div className="text-gray-600">{filename}</div>
                      {!hasFile && (
                        <div className="text-[11px] text-red-500">
                          No file stored for this TMX entry
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded text-xs ${
                          hasFile
                            ? "text-white hover:opacity-90"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                        style={hasFile ? { backgroundColor: "#00a7e1" } : {}}
                        onClick={() =>
                          hasFile &&
                          downloadUserTMXFile(tmxModalUser.id, item)
                        }
                        disabled={!hasFile}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded border text-sm hover:bg-gray-100"
                onClick={() => {
                  setTmxModalUser(null);
                  setTmxModalItems([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminology modal for Draft Users */}
      {draftTermModalDraft && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4">
            <h3 className="text-lg font-semibold mb-2">
              Terminology for draft{" "}
              {draftTermModalDraft.email || "(no email)"}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              View terminology entries captured in the workflow builder. Only
              entries with a stored file can be downloaded; others are
              prompt-only.
            </div>

            <div className="max-h-64 overflow-y-auto divide-y border rounded">
              {draftTermModalItems.map((item) => {
                const pair = item.pair_label || "Language pair";
                const filename = item.filename || "terminology.csv";
                const hasFile = !!item.has_file;

                return (
                  <div
                    key={item.id}
                    className="p-2 flex items-center justify-between gap-2"
                  >
                    <div className="text-xs leading-snug">
                      <div className="font-semibold">{pair}</div>
                      <div className="text-gray-600">{filename}</div>
                      {!hasFile && (
                        <div className="text-[11px] text-red-500">
                          Prompt-only entry (no file stored)
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded text-xs ${
                          hasFile
                            ? "text-white hover:opacity-90"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                        style={hasFile ? { backgroundColor: "#00a7e1" } : {}}
                        onClick={() =>
                          hasFile &&
                          handleDraftDownloadTerminology(draftTermModalDraft)
                        }
                        disabled={!hasFile}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded border text-sm hover:bg-gray-100"
                onClick={() => {
                  setDraftTermModalDraft(null);
                  setDraftTermModalItems([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TMX modal for Draft Users */}
      {draftTmxModalDraft && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4">
            <h3 className="text-lg font-semibold mb-2">
              TMX for draft{" "}
              {draftTmxModalDraft.email || "(no email)"}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              View TMX entries captured in the workflow builder. Only
              entries with a stored file can be downloaded.
            </div>

            <div className="max-h-64 overflow-y-auto divide-y border rounded">
              {draftTmxModalItems.map((item) => {
                const pair = item.pair_label || "Language pair";
                const filename = item.filename || "tmx.tmx";
                const hasFile = !!item.has_file;

                return (
                  <div
                    key={item.id}
                    className="p-2 flex items-center justify-between gap-2"
                  >
                    <div className="text-xs leading-snug">
                      <div className="font-semibold">{pair}</div>
                      <div className="text-gray-600">{filename}</div>
                      {!hasFile && (
                        <div className="text-[11px] text-red-500">
                          No file stored for this TMX entry
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded text-xs ${
                          hasFile
                            ? "text-white hover:opacity-90"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                        style={hasFile ? { backgroundColor: "#00a7e1" } : {}}
                        onClick={() =>
                          hasFile &&
                          handleDraftDownloadTMX(draftTmxModalDraft, item)
                        }
                        disabled={!hasFile}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded border text-sm hover:bg-gray-100"
                onClick={() => {
                  setDraftTmxModalDraft(null);
                  setDraftTmxModalItems([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

	</div>
  );
}
